import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import David from "@/components/David";
import AdvisorModal from "@/components/AdvisorModal";
import LowCreditNudge from "@/components/LowCreditNudge";
import PitchTutorial, { shouldShowPitchTutorial } from "@/components/PitchTutorial";
import CreditBalance from "@/components/CreditBalance";
import AdvisorSessionsInline from "@/components/AdvisorSessionsInline";
import ContinueOnDesktopNotice from "@/components/ContinueOnDesktopNotice";
import ImproveSlideDesign from "@/components/ImproveSlideDesign";
import SlidePreviewBody from "@/components/SlidePreviewBody";
import PlayDeckModal from "@/components/PlayDeckModal";
import DraftHistoryPanel from "@/components/DraftHistoryPanel";
import OverflowBanner from "@/components/OverflowBanner";
import WizardFooterNav from "@/components/WizardFooterNav";
import WizardSlidePreviewAndNav from "@/components/WizardSlidePreviewAndNav";
import WizardChrome, { TOTAL_STEPS, CORE_SLIDES_COUNT } from "@/components/WizardChrome";
import IndustryBadge from "@/components/IndustryBadge";
import MyExports from "@/components/MyExports";
import PhaseIndicator from "@/components/PhaseIndicator";
import { toast } from "sonner";
import { extractErrorMessage, toastApiError } from "@/lib/apiError";
import { getSlidePlaceholder, GENERIC_PLACEHOLDER } from "@/lib/slidePlaceholders";
import MarketSizeForm from "@/components/MarketSizeForm";
import BusinessModelForm from "@/components/BusinessModelForm";
import CapitalAskForm from "@/components/CapitalAskForm";
import CompetitiveForm from "@/components/CompetitiveForm";
import CoverSnapshotForm from "@/components/CoverSnapshotForm";
import FactsQueuePanel from "@/components/FactsQueuePanel";
import ReviewPanel, { ReviewGateCard } from "@/components/ReviewPanel";
import {
  ArrowLeft, ArrowRight, FloppyDisk, Sparkle, CheckCircle, WarningCircle, XCircle,
  DownloadSimple, Books, Shield, Plus, Trash, UploadSimple, Image as ImageIcon,
  Info, Palette, MagicWand, FileImage, ShareNetwork as Share, Copy, EyeSlash, Eye,
  ClockCounterClockwise, CaretDown,
  ChatCircleText, BookOpen, X, PlayCircle, CaretRight,
} from "@phosphor-icons/react";
import { useSimulatedProgress, ExportProgressOverlay } from "@/components/ProgressDonut";

const C = {
  bgDark: "#000", panel: "#0A2826", panelAlt: "#103532",
  teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B",
};

// Per-slide "what photos & diagrams would work here?" suggestions —
// shown directly under the upload control on each slide so founders know
// exactly what visuals make the slide land harder with funders.
const IMAGE_SUGGESTIONS = {
  1:  "A clean product hero shot or company logo lockup. Optionally: a single headline metric (e.g. '12k MAUs') as a number card. Keep it simple — this slide sets the visual tone.",
  2:  "A photo of the people actually affected by the problem (your customer in their day-to-day), a before/after comparison, or a chart showing the size of the pain (cost, time wasted, market loss). Make funders FEEL the problem.",
  3:  "Product screenshots or a short workflow diagram showing how your solution works step-by-step. If you have a physical product — a clean studio shot. Avoid wireframes; use the real UI.",
  4:  "A TAM / SAM / SOM nested bubble chart, a market-size bar chart with credible sources, OR a map of the geography you're addressing (great for Africa-first plays). Cite every number.",
  5:  "A unit-economics one-pager (cost-per-unit → price → margin), a pricing table, or a flow diagram showing money in vs money out. One picture beats a paragraph of explanation.",
  6:  "Hockey-stick or step-change growth chart (users, revenue, GMV, retention). Customer-logo wall if you have B2B clients. Testimonial pull-quotes with a face + title work brilliantly here.",
  7:  "A funnel diagram (awareness → trial → paid → expansion), CAC payback chart, or a map of your distribution channels & partnerships. Show HOW you reach customers, not just that you will.",
  8:  "A 2x2 competitive positioning matrix (axes your category cares about), a feature comparison table, or a quadrant placing you vs. the named competitors. Be honest — funders verify.",
  9:  "Headshots of the founding team with one-line credentials (ex-Andela, ex-Safaricom, etc.). Optionally: an org chart or a 'gaps we're hiring for' visual. Faces > LinkedIn URLs.",
  10: "A 3+5-year revenue/EBITDA chart, a cash-burn vs. runway chart, or a stacked-bar of revenue streams over time. Pair with a callout for the year of break-even.",
  11: "A pie chart or stacked-bar showing use of funds (Product / Hires / GTM / Working capital). A cap-table 'before & after' if relevant. Include your milestones-per-tranche.",
  12: "An index/checklist screenshot showing the documents available in your data room (financials, customer contracts, IP, regulatory). No need to upload sensitive docs — just prove they exist.",
};

const FALLBACK_IMAGE_HINT = "Add product shots, team photos, charts or diagrams that go on this slide.";

/** Slides whose visual layout is locked to a canonical form — no design
 *  gallery, no free-text editor, no overflow banner. Iter59 addition
 *  extracted from the iter54/55/57/58/59 rollout. Add new locked slides
 *  here and every consumer (OverflowBanner, WizardSlidePreviewAndNav
 *  gallery guard, SlidePreviewBody layout locks) picks up the change. */
const LOCKED_LAYOUT_SLIDES = new Set([1, 4, 5, 8, 9, 10, 11, 12]);

/** Slides where photo/diagram uploads are hidden per iter63 user spec —
 *  these use canonical fixed layouts that don't include an image slot. */
const NO_PHOTOS_SLIDES = new Set([4, 5, 11]);

/* Auto-pick the founder's brand colour from their just-uploaded logo. Loads
   the file into a <canvas>, samples every pixel, and returns the most-common
   colour excluding near-white / near-black / low-saturation greys. Returns
   a `#rrggbb` string, or `null` if we can't confidently pick one. Runs
   entirely client-side — no dependency, no server round-trip. */
function extractDominantColor(file) {
  return new Promise((resolve) => {
    if (!file) return resolve(null);
    const reader = new FileReader();
    reader.onerror = () => resolve(null);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => resolve(null);
      img.onload = () => {
        try {
          // Shrink into a 64×64 grid for fast pixel sampling — plenty for
          // dominant-hue detection while staying instant on mobile.
          const size = 64;
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d", { willReadFrequently: true });
          if (!ctx) return resolve(null);
          // Flatten transparent PNG logos onto a neutral background so alpha
          // doesn't distort the sample.
          ctx.fillStyle = "#7f7f7f";
          ctx.fillRect(0, 0, size, size);
          ctx.drawImage(img, 0, 0, size, size);
          const data = ctx.getImageData(0, 0, size, size).data;
          // Bucket by quantised RGB (12 bits) so subtle variations collapse.
          const buckets = new Map();
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
            if (a < 200) continue;                       // skip mostly-transparent pixels
            const max = Math.max(r, g, b), min = Math.min(r, g, b);
            const brightness = (r + g + b) / 3;
            const saturation = max === 0 ? 0 : (max - min) / max;
            // Skip near-white / near-black / grey pixels — they're the
            // background or ink, not the brand colour.
            if (brightness > 235 || brightness < 20 || saturation < 0.18) continue;
            const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
            const cur = buckets.get(key) || { r: 0, g: 0, b: 0, n: 0 };
            cur.r += r; cur.g += g; cur.b += b; cur.n += 1;
            buckets.set(key, cur);
          }
          if (buckets.size === 0) return resolve(null);
          let best = null;
          for (const v of buckets.values()) {
            if (!best || v.n > best.n) best = v;
          }
          if (!best || best.n < 8) return resolve(null);  // require some evidence
          const r = Math.round(best.r / best.n);
          const g = Math.round(best.g / best.n);
          const b = Math.round(best.b / best.n);
          const toHex = (n) => n.toString(16).padStart(2, "0");
          resolve(`#${toHex(r)}${toHex(g)}${toHex(b)}`);
        } catch (_) {
          resolve(null);
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

/* Given a `#rrggbb` hex, return a complementary accent colour (hue rotated
   180°, saturation/lightness preserved). Used to auto-pair the extracted
   brand colour with a stand-out accent for the deck's headers, badges, and
   number highlights. Falls back to the input if parsing fails. */
function complementaryAccent(hex) {
  if (!/^#?[0-9a-f]{6}$/i.test(hex)) return hex;
  const h = hex.startsWith("#") ? hex.slice(1) : hex;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : (l > 0.5 ? d / (2 - max - min) : d / (max + min));
  let hue = 0;
  if (d !== 0) {
    if (max === r) hue = ((g - b) / d) % 6;
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue *= 60;
    if (hue < 0) hue += 360;
  }
  // Rotate +180° for a strong complement. Nudge saturation up a touch so
  // very muted logos still yield a visible accent.
  const nh = (hue + 180) % 360;
  const ns = Math.min(1, Math.max(0.35, s));
  // Keep the accent bright enough to read on the primary-colour backgrounds.
  const nl = Math.min(0.62, Math.max(0.42, l));
  const c = (1 - Math.abs(2 * nl - 1)) * ns;
  const x = c * (1 - Math.abs(((nh / 60) % 2) - 1));
  const m = nl - c / 2;
  let [rr, gg, bb] = [0, 0, 0];
  if (nh < 60)      [rr, gg, bb] = [c, x, 0];
  else if (nh < 120)[rr, gg, bb] = [x, c, 0];
  else if (nh < 180)[rr, gg, bb] = [0, c, x];
  else if (nh < 240)[rr, gg, bb] = [0, x, c];
  else if (nh < 300)[rr, gg, bb] = [x, 0, c];
  else              [rr, gg, bb] = [c, 0, x];
  const toHex = (n) => Math.round((n + m) * 255).toString(16).padStart(2, "0");
  return `#${toHex(rr)}${toHex(gg)}${toHex(bb)}`;
}

const ClinqLogo = () => (
  <div className="flex items-center gap-3">
    <span className="relative inline-flex items-baseline font-extrabold tracking-tight text-2xl"
      style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em", color: C.teal }}>
      Cl
      <span className="relative inline-block">
        ı
        <span aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 rounded-full clinq-dot-glow"
          style={{ backgroundColor: C.orange, height: "0.5rem", width: "0.5rem", bottom: "1.05em" }}></span>
      </span>
      nq
    </span>
    <div className="hidden sm:flex flex-col leading-none border-l pl-3 border-white/15">
      <span className="text-[9px] uppercase tracking-[0.22em] font-semibold" style={{ color: C.tealSoft }}>Pitch</span>
      <span className="text-[9px] uppercase tracking-[0.22em] font-semibold mt-0.5 text-white/55">Builder</span>
    </div>
  </div>
);

export default function PitchEditor() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deck, setDeck] = useState(null);
  const [meta, setMeta] = useState(null);
  const [activeSlide, setActiveSlide] = useState(1);
  const [activeExtraId, setActiveExtraId] = useState(null);
  const [content, setContent] = useState("");
  const [findings, setFindings] = useState([]);
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [exportDone, setExportDone] = useState(false);
  const [exportStage, setExportStage] = useState("queued");
  const [createdBlob, setCreatedBlob] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [prevExportsOpen, setPrevExportsOpen] = useState(false);
  const [view, setView] = useState("slides"); // slides | check | brand
  const [fixHint, setFixHint] = useState(null); // {code, reason, slide_num} — set by Fix-It shortcuts
  const [check, setCheck] = useState(null);
  const [extraNotes, setExtraNotes] = useState("");
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [advisorOpen, setAdvisorOpen] = useState(false);
  const [importPromptOpen, setImportPromptOpen] = useState(false);
  const [userModels, setUserModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState("");
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const saveTimer = useRef(null);
  const [predrafting, setPredrafting] = useState(false);
  const predraftFiredRef = useRef(false);

  const runPredraft = useCallback(async () => {
    setPredrafting(true);
    const tid = toast.loading("David is prefilling your slides — takes ~30 seconds…");
    try {
      const { data } = await api.post(`/pitch/decks/${deckId}/predraft`);
      const slides = (data.slides || []).filter((s) => s && s.num !== 7);
      setDeck((d) => ({ ...d, slides, predrafted_at: data.predrafted_at }));
      setContent((prev) => {
        const cur = slides.find((s) => s.num === activeSlide);
        return activeExtraId ? prev : (cur?.content || prev);
      });
      toast.success(`Starter bullets added to ${(data.filled || []).length} slides — refine each one with real numbers.`, { id: tid, duration: 8000 });
    } catch (err) {
      // 402 credit shortage is toasted globally (api.js) with a Buy credits CTA.
      if (err.isCreditShortage) toast.dismiss(tid);
      else toast.error(extractErrorMessage(err, "Prefill failed"), { id: tid });
    } finally {
      setPredrafting(false);
    }
  }, [deckId, activeSlide, activeExtraId]);

  useEffect(() => {
    if (!deck || predraftFiredRef.current) return;
    if (searchParams.get("predraft") === "1") {
      predraftFiredRef.current = true;
      const sp = new URLSearchParams(searchParams);
      sp.delete("predraft");
      setSearchParams(sp, { replace: true });
      if (!deck.predrafted_at) runPredraft();
    }
  }, [deck, searchParams, setSearchParams, runPredraft]);

  const deckIsBlank = useMemo(
    () => !!deck && (deck.slides || []).every((s) => !(s.content || "").trim()),
    [deck]
  );

  const predraftOffer = deck && !deck.predrafted_at && deckIsBlank && (
    <div className="mb-5 rounded-lg border px-4 py-3 flex flex-wrap items-center gap-3"
      style={{ borderColor: "rgba(123, 211, 206, 0.35)", backgroundColor: "rgba(10, 139, 133, 0.10)" }}
      data-testid="predraft-offer">
      <Sparkle size={16} weight="fill" style={{ color: C.tealSoft }} className="shrink-0" />
      <div className="text-[13px] leading-relaxed flex-1 min-w-[240px]">
        <span className="font-bold text-white/90">Clinq can prefill your slides for you.</span>{" "}
        <span className="text-white/70">Starter bullets on every slide, drafted from your snapshot and answers — you refine, not start from blank.</span>
      </div>
      <button onClick={runPredraft} disabled={predrafting} data-testid="predraft-btn"
        className="px-4 py-2 rounded-md text-[12px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
        style={{ backgroundColor: C.teal, color: "#fff" }}>
        {predrafting ? "Prefilling…" : "Prefill my slides · 10 credits"}
      </button>
    </div>
  );

  useEffect(() => {
    (async () => {
      try {
        const [d, m, mm] = await Promise.all([
          api.get(`/pitch/decks/${deckId}`),
          api.get("/pitch/meta"),
          api.get("/models").catch(() => ({ data: [] })),
        ]);
        // Iter76 — defensive parity guard. Go-to-Market (slide.num=7)
        // was removed from the deck flow in iter56 but decks created
        // before that migration still carry a slide.num=7 entry in
        // Mongo. Strip it here (at the single source of truth) so
        // EVERY downstream consumer — the wizard nav, in-editor
        // preview, Play Deck, share link, funder-ready check, and the
        // PPTX export trigger — automatically renders the same clean
        // set of slides. Without this, decks silently diverged
        // between the preview surfaces and the "Create your pitch
        // deck" output.
        const cleanedDeck = {
          ...d.data,
          slides: (d.data.slides || []).filter((s) => s && s.num !== 7),
        };
        setDeck(cleanedDeck); setMeta(m.data);
        setUserModels(mm.data || []);
        if ((mm.data || []).length > 0) setSelectedModelId(mm.data[0].model_id);
        const s = cleanedDeck.slides.find((x) => x.num === 1);
        setContent(s?.content || "");
        // Tutorial is fired the first time the founder LANDS on the Brand
        // step (see the effect below). We no longer auto-open on load.
        // Land brand-new wizard decks on step 1 (Brand & style) so founders
        // upload logo + set brand colour before drafting slides. Returning
        // decks (with any brand config or slide content) stay on slides.
        if (d.data.flow_version === "wizard") {
          const hasBrand = !!(d.data.logo || d.data.tagline
            || (d.data.brand_color && d.data.brand_color !== "#0A8B85"));
          const hasContent = (d.data.slides || []).some(
            (sl) => (sl.content || "").trim().length > 20);
          if (!hasBrand && !hasContent) setView("brand");
        }
      } catch {
        toast.error("Deck not found");
        navigate("/dashboard");
      }
    })();
  }, [deckId, navigate]);

  // The "How this works" tutorial fires the first time the founder lands on
  // the Brand step of THIS deck. Dismissed state is persisted per-deck in
  // localStorage by the PitchTutorial component, so every new pitch starts
  // with a fresh walkthrough (previously it only fired once per device).
  useEffect(() => {
    if (view === "brand" && shouldShowPitchTutorial(deckId)) {
      // Small delay so it feels like it arrived AFTER the Brand step painted,
      // not layered simultaneously.
      const t = setTimeout(() => setTutorialOpen(true), 500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [view, deckId]);

  const switchSlide = (num, extraId = null) => {
    setActiveSlide(num);
    setActiveExtraId(extraId);
    const s = deck.slides.find((x) => x.num === num);
    if (extraId) {
      const ex = (s?.extras || []).find((x) => x.id === extraId);
      setContent(ex?.content || "");
    } else {
      setContent(s?.content || "");
    }
    setFindings([]);
  };

  const saveSlide = useCallback(async () => {
    if (!deck) return;
    setSaving(true);
    try {
      const url = activeExtraId
        ? `/pitch/decks/${deckId}/slide/${activeSlide}/extras/${activeExtraId}`
        : `/pitch/decks/${deckId}/slide/${activeSlide}`;
      const { data } = await api.put(url, { content });
      setFindings(data.findings || []);
      setDeck((d) => ({
        ...d,
        slides: d.slides.map((s) => {
          if (s.num !== activeSlide) return s;
          if (activeExtraId) {
            return {
              ...s,
              extras: (s.extras || []).map((x) => x.id === activeExtraId ? { ...x, content } : x),
            };
          }
          return { ...s, content };
        }),
      }));
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  }, [deckId, activeSlide, activeExtraId, content, deck]);

  // Debounced auto-save on content change
  useEffect(() => {
    if (!deck) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { saveSlide(); }, 1200);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  // Listen for design-prefs saves from the "Change this slide's design" modal
  // and merge them into the local deck state so the HTML preview immediately
  // re-renders with the newly chosen layout / infographic / etc. When the
  // save came from a sub-slide (detail.extraId), we patch that extra's
  // design_prefs instead of the parent slide's.
  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      if (!detail.slideNum || !detail.prefs) return;
      setDeck((d) => {
        if (!d) return d;
        return {
          ...d,
          slides: d.slides.map((s) => {
            if (s.num !== detail.slideNum) return s;
            if (detail.extraId) {
              return {
                ...s,
                extras: (s.extras || []).map((x) => x.id !== detail.extraId ? x : {
                  ...x,
                  design_prefs: { ...(x.design_prefs || {}), ...detail.prefs },
                }),
              };
            }
            return { ...s, design_prefs: { ...(s.design_prefs || {}), ...detail.prefs } };
          }),
        };
      });
    };
    window.addEventListener("clinq:design-prefs-saved", handler);
    return () => window.removeEventListener("clinq:design-prefs-saved", handler);
  }, []);

  // Listen for premium AI rewrites so the local deck state (and thus the
  // preview + Play deck) picks up the fresh content immediately without a
  // full deck refetch.
  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      if (!detail.slideNum || typeof detail.content !== "string") return;
      setDeck((d) => {
        if (!d) return d;
        return {
          ...d,
          slides: d.slides.map((s) => {
            if (s.num !== detail.slideNum) return s;
            if (detail.extraId) {
              return {
                ...s,
                extras: (s.extras || []).map((x) => x.id !== detail.extraId ? x : {
                  ...x, content: detail.content,
                }),
              };
            }
            return { ...s, content: detail.content };
          }),
        };
      });
      // Also sync the local textarea state for the active slide so the founder
      // sees the new content in the editor without a full page refresh.
      if (activeSlide === detail.slideNum && !detail.extraId) {
        setContent(detail.content);
      }
    };
    window.addEventListener("clinq:premium-rewrite-applied", handler);
    return () => window.removeEventListener("clinq:premium-rewrite-applied", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlide]);

  const draft = async () => {
    // Snapshot the current content BEFORE the AI overwrites it, so we can
    // offer a one-click undo in the success toast. Undo is content-only —
    // the credits already spent on the draft are NOT refunded (matches the
    // stated behaviour: "no credits are returned").
    const snapshot = {
      mode: activeExtraId ? "extra" : "main",
      slideNum: activeSlide,
      extraId: activeExtraId,
      content,
      findings,
    };

    const undoDraft = async () => {
      try {
        if (snapshot.mode === "extra") {
          await api.put(
            `/pitch/decks/${deckId}/slide/${snapshot.slideNum}/extras/${snapshot.extraId}`,
            { content: snapshot.content },
          );
          setDeck((d) => ({
            ...d,
            slides: d.slides.map((s) => s.num !== snapshot.slideNum ? s : {
              ...s,
              extras: (s.extras || []).map((x) => x.id === snapshot.extraId ? { ...x, content: snapshot.content } : x),
            }),
          }));
        } else {
          await api.put(`/pitch/decks/${deckId}/slide/${snapshot.slideNum}`, {
            content: snapshot.content,
          });
          setDeck((d) => ({
            ...d,
            slides: d.slides.map((s) => s.num === snapshot.slideNum ? { ...s, content: snapshot.content } : s),
          }));
        }
        // Only mutate the visible editor if the user is still looking at the
        // same slide/extra they drafted — otherwise leave their current view
        // untouched (deck state already reflects the revert).
        if (activeSlide === snapshot.slideNum && activeExtraId === snapshot.extraId) {
          setContent(snapshot.content);
          setFindings(snapshot.findings || []);
        }
        toast.success("Reverted to your previous draft");
      } catch (err) {
        toastApiError(err, "Undo failed. Please refresh.", { context: { deck_id: deckId, action: "undo_draft" } });
      }
    };

    setDrafting(true);
    try {
      if (activeExtraId) {
        // Drafting a sub-slide — just re-run the base slide draft, write into the extra
        const { data } = await api.post(`/pitch/decks/${deckId}/draft/${activeSlide}`, {
          extra_notes: extraNotes || `This is a sub-slide expanding on slide ${activeSlide}.`,
        });
        // Save into the extra
        setContent(data.content);
        setFindings(data.findings || []);
        await api.put(`/pitch/decks/${deckId}/slide/${activeSlide}/extras/${activeExtraId}`, { content: data.content });
        setDeck((d) => ({
          ...d,
          slides: d.slides.map((s) => s.num !== activeSlide ? s : {
            ...s,
            extras: (s.extras || []).map((x) => x.id === activeExtraId ? { ...x, content: data.content } : x),
          }),
        }));
      } else {
        const { data } = await api.post(`/pitch/decks/${deckId}/draft/${activeSlide}`, { extra_notes: extraNotes });
        setContent(data.content);
        setFindings(data.findings || []);
        setDeck((d) => ({
          ...d,
          slides: d.slides.map((s) => s.num === activeSlide ? { ...s, content: data.content } : s),
        }));
      }
      // Toast with an Undo action. 10s window is generous — long enough for
      // a founder to skim the new draft, short enough to auto-dismiss cleanly.
      const hadPrevious = (snapshot.content || "").trim().length > 0;
      toast.success("Slide drafted by AI", hadPrevious ? {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: undoDraft,
        },
        description: "Undo restores your previous draft. Credits already spent are not refunded.",
      } : { duration: 4000 });
      window.dispatchEvent(new Event("clinq:credits-changed"));
      // Notify any open DraftHistoryPanel that a new entry was appended.
      window.dispatchEvent(new Event("clinq:draft-history-changed"));
    } catch (e) {
      handleCreditOrError(e, "AI draft failed", {
        action: "ai_draft",
        slide_num: activeSlide,
        extra_id: activeExtraId || null,
      });
    } finally {
      setDrafting(false);
    }
  };

  const insertFundingPlan = async () => {
    try {
      const q = selectedModelId ? `?model_id=${encodeURIComponent(selectedModelId)}` : "";
      const { data } = await api.get(`/pitch/decks/${deckId}/funding-suggestion${q}`);
      const combined = content && content.trim()
        ? `${content.trim()}\n\n${data.content}`
        : data.content;
      setContent(combined);
      toast.success(`Imported funding plan from ${data.model_name}`);
    } catch (e) {
      toastApiError(e, "Could not import from your financial model.", {
        context: { deck_id: deckId, action: "import_funding_plan", model_id: selectedModelId || null },
      });
    }
  };

  const addExtra = async (num) => {
    try {
      const { data } = await api.post(`/pitch/decks/${deckId}/slide/${num}/extras`, {});
      setDeck((d) => ({
        ...d,
        slides: d.slides.map((s) => s.num !== num ? s : { ...s, extras: [...(s.extras || []), data] }),
      }));
      switchSlide(num, data.id);
      toast.success("Sub-slide added");
    } catch (e) {
      toastApiError(e, "Could not add sub-slide", {
        context: { deck_id: deckId, action: "add_subslide", slide_num: num },
        onBuyCredits: () => navigate("/credits"),
      });
    }
  };

  const deleteExtra = async (num, extraId, e) => {
    e?.preventDefault?.(); e?.stopPropagation?.();
    if (!window.confirm("Delete this sub-slide?")) return;
    try {
      await api.delete(`/pitch/decks/${deckId}/slide/${num}/extras/${extraId}`);
      setDeck((d) => ({
        ...d,
        slides: d.slides.map((s) => s.num !== num ? s : { ...s, extras: (s.extras || []).filter((x) => x.id !== extraId) }),
      }));
      if (activeSlide === num && activeExtraId === extraId) switchSlide(num);
      toast.success("Sub-slide removed");
    } catch {
      toast.error("Could not delete sub-slide");
    }
  };

  const [importPct, setImportPct] = useState(0);          // 0–100 upload bytes
  const [importPhase, setImportPhase] = useState("idle"); // idle | uploading | processing

  const importDeck = async (file) => {
    if (!file) return;
    if (file.size > 40 * 1024 * 1024) {
      toast.error("File too large — max 40 MB.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setImporting(true);
    setImportPct(0);
    setImportPhase("uploading");
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Step 1 — upload + extract + credit charge + job creation (fast, <30s)
      const { data: job } = await api.post(`/pitch/decks/${deckId}/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const pct = Math.round((evt.loaded / evt.total) * 100);
          setImportPct(pct);
          if (pct >= 100) setImportPhase("processing");
        },
      });
      // Step 2 — poll the job until Claude mapping finishes (immune to CF 100s cap)
      setImportPhase("processing");
      window.dispatchEvent(new Event("clinq:credits-changed"));
      const result = await _pollImportJob(job.job_id);
      // Step 3 — refresh deck (apply the same iter76 filter used at
      // initial load so re-fetches don't reintroduce a stale slide 7).
      const { data: refreshed } = await api.get(`/pitch/decks/${deckId}`);
      const cleanedRefreshed = {
        ...refreshed,
        slides: (refreshed.slides || []).filter((s) => s && s.num !== 7),
      };
      setDeck(cleanedRefreshed);
      const s = cleanedRefreshed.slides.find((x) => x.num === activeSlide);
      setContent(s?.content || "");
      toast.success(
        `Imported · ${result.applied_to_core} slides mapped · ${result.applied_as_extras} sub-slides`,
        { duration: 8000 }
      );
    } catch (e) {
      handleCreditOrError(e, "Import failed", { action: "import_financial_model" });
    } finally {
      setImporting(false);
      setImportPhase("idle");
      setImportPct(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Poll the import job every 2s for up to 5 minutes. Throws on error/timeout.
  const _pollImportJob = async (jobId) => {
    const MAX_ATTEMPTS = 150;   // 150 × 2s = 5 min
    const DELAY_MS = 2000;
    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      // Eslint disable for sequential awaits inside a polling loop
      // eslint-disable-next-line no-await-in-loop
      const { data } = await api.get(`/pitch/decks/${deckId}/import/${jobId}`);
      if (data.status === "done") return data;
      if (data.status === "error") {
        const detail = data.error || "AI mapping failed";
        const err = new Error(detail);
        err.response = { status: 502, data: { detail } };
        throw err;
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, DELAY_MS));
    }
    const err = new Error("Import is taking longer than expected. Refresh in a minute — your deck will be updated when AI mapping finishes.");
    err.response = { status: 504, data: { detail: err.message } };
    throw err;
  };

  const uploadSlideImage = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const qs = activeExtraId ? `?extra_id=${encodeURIComponent(activeExtraId)}` : "";
      const { data } = await api.post(`/pitch/decks/${deckId}/slide/${activeSlide}/images${qs}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDeck((d) => ({
        ...d,
        slides: d.slides.map((s) => s.num !== activeSlide ? s : (
          activeExtraId
            ? { ...s, extras: (s.extras || []).map((x) => x.id !== activeExtraId ? x : {
                ...x,
                images: [...((x.images || []).filter((im) => im?.source !== "auto_generated")), data],
              }) }
            : { ...s, images: [...((s.images || []).filter((im) => im?.source !== "auto_generated")), data] }
        )),
      }));
      toast.success("Image added");
    } catch (e) {
      toastApiError(e, "Image upload failed", { context: { deck_id: deckId, action: "image_upload" } });
    } finally {
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const deleteSlideImage = async (imgId) => {
    if (!window.confirm("Remove this image?")) return;
    try {
      const qs = activeExtraId ? `?extra_id=${encodeURIComponent(activeExtraId)}` : "";
      await api.delete(`/pitch/decks/${deckId}/slide/${activeSlide}/images/${imgId}${qs}`);
      setDeck((d) => ({
        ...d,
        slides: d.slides.map((s) => s.num !== activeSlide ? s : (
          activeExtraId
            ? { ...s, extras: (s.extras || []).map((x) => x.id !== activeExtraId ? x : {
                ...x, images: (x.images || []).filter((im) => im.id !== imgId),
              }) }
            : { ...s, images: (s.images || []).filter((x) => x.id !== imgId) }
        )),
      }));
    } catch {
      toast.error("Could not remove image");
    }
  };

  // Spill overflow bullets into a new sub-slide (2 credits). The banner +
  // network call live inside <OverflowBanner/> — this handler just syncs
  // deck state after the spill succeeds and optionally jumps to the new
  // sub-slide when the toast's "Open sub-slide" action fires.
  const OVERFLOW_THRESHOLD = 5;
  const bulletCount = useMemo(() => (content || "")
    .split("\n")
    .map((l) => l.replace(/^[-*•●▪]\s*/, "").replace(/^\d+[.)]\s*/, "").trim())
    .filter(Boolean).length, [content]);
  const onOverflowSpilled = (data) => {
    if (data.__openSubSlide) {
      // Bypass switchSlide's deck-lookup — the deck state may not yet
      // include the freshly-created extra (setDeck is async, and this
      // callback fires from a toast closure captured before that setDeck
      // was applied). Setting content directly from the endpoint response
      // avoids the empty-content -> debounced-autosave -> data-loss chain.
      setActiveSlide(activeSlide);
      setActiveExtraId(data.extra.id);
      setContent(data.extra.content || "");
      setFindings([]);
      return;
    }
    setContent(data.content);
    setDeck((d) => ({
      ...d,
      slides: d.slides.map((s) => s.num !== activeSlide ? s : {
        ...s,
        content: data.content,
        extras: [...(s.extras || []), data.extra],
      }),
    }));
  };
  const [regenerating, setRegenerating] = useState(false);
  const regenerateSlidePhoto = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const params = new URLSearchParams({ regenerate: "true" });
      if (activeExtraId) params.set("extra_id", activeExtraId);
      const { data } = await api.post(
        `/pitch/decks/${deckId}/slide/${activeSlide}/auto-photo?${params.toString()}`
      );
      setDeck((d) => ({
        ...d,
        slides: d.slides.map((s) => s.num !== activeSlide ? s : (
          activeExtraId
            ? { ...s, extras: (s.extras || []).map((x) => x.id !== activeExtraId ? x : {
                ...x,
                images: [
                  ...((x.images || []).filter((im) => im?.source !== "auto_generated")),
                  data.image,
                ],
              }) }
            : { ...s, images: [
                ...((s.images || []).filter((im) => im?.source !== "auto_generated")),
                data.image,
              ] }
        )),
      }));
      window.dispatchEvent(new Event("clinq:credits-changed"));
      toast.success(`Fresh photo generated · ${data.cost_credits || 1} credit used`);
    } catch (err) {
      const detail = extractErrorMessage(err, "Regenerate failed. Please try again.");
      toast.error(typeof detail === "string" ? detail : "Regenerate failed. Please try again.");
    } finally {
      setRegenerating(false);
    }
  };

  // Generate the FIRST auto-photo for a sub-slide (3 credits). Parent slides
  // get their initial auto-photo via ImproveSlideDesign's design gallery, but
  // sub-slides don't have that entry point — this is the wired-up button.
  const [generatingSubPhoto, setGeneratingSubPhoto] = useState(false);
  const generateSubSlidePhoto = async () => {
    if (!activeExtraId || generatingSubPhoto) return;
    setGeneratingSubPhoto(true);
    try {
      const params = new URLSearchParams({ extra_id: activeExtraId });
      const { data } = await api.post(
        `/pitch/decks/${deckId}/slide/${activeSlide}/auto-photo?${params.toString()}`
      );
      setDeck((d) => ({
        ...d,
        slides: d.slides.map((s) => s.num !== activeSlide ? s : {
          ...s,
          extras: (s.extras || []).map((x) => x.id !== activeExtraId ? x : {
            ...x,
            images: [
              ...((x.images || []).filter((im) => im?.source !== "auto_generated")),
              data.image,
            ],
          }),
        }),
      }));
      window.dispatchEvent(new Event("clinq:credits-changed"));
      toast.success(`Photo generated · ${data.cost_credits || 3} credits used — upload your own to replace or regenerate for 1 cr`);
    } catch (err) {
      const detail = extractErrorMessage(err, "Photo generation failed. Please try again.");
      toast.error(typeof detail === "string" ? detail : "Photo generation failed. Please try again.");
    } finally {
      setGeneratingSubPhoto(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/pitch/decks/${deckId}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDeck((d) => ({ ...d, logo: data.logo }));
      toast.success("Logo uploaded");
      // Auto-extract the dominant colour from the just-uploaded logo and save
      // it as the brand colour so the deck picks up the founder's brand
      // instantly. Runs client-side (canvas pixel sampling) — no dependency.
      try {
        const dominant = await extractDominantColor(file);
        if (dominant) {
          const accent = complementaryAccent(dominant);
          await api.put(`/pitch/decks/${deckId}/brand`, {
            brand_color: dominant,
            accent_color: accent,
          });
          setDeck((d) => ({ ...d, brand_color: dominant, accent_color: accent }));
          toast.success(`Brand palette auto-picked: ${dominant} + ${accent} accent`);
        }
      } catch (_) {
        /* Non-fatal — founder can still pick a colour manually. */
      }
    } catch (e) {
      toastApiError(e, "Logo upload failed", { context: { deck_id: deckId, action: "logo_upload" } });
    } finally {
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    try {
      await api.delete(`/pitch/decks/${deckId}/logo`);
      setDeck((d) => ({ ...d, logo: null }));
    } catch { toast.error("Could not remove logo"); }
  };

  const updateBrandColor = async (color) => {
    setDeck((d) => ({ ...d, brand_color: color }));
    try {
      await api.put(`/pitch/decks/${deckId}/brand`, { brand_color: color });
    } catch {
      toast.error("Could not save colour");
    }
  };

  /**
   * Async PPTX export driver. POSTs to enqueue a job (charges 25 credits
   * server-side, idempotent via Idempotency-Key), polls status every 2s
   * until ready/failed, then downloads the blob. Returns the Blob on
   * success. Times out client-side after ~2 minutes so the UI can't wait
   * forever if the server never publishes a terminal status.
   */
  const runAsyncExport = async () => {
    // One UUID per click → server dedupes double-fires without double-charging.
    const idemKey =
      (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    // 1. Enqueue
    const enq = await api.post(
      `/pitch/decks/${deckId}/export`,
      {},
      { headers: { "Idempotency-Key": idemKey } },
    );
    const jobId = enq.data.job_id;
    const pollMs = enq.data.poll_interval_ms || 2000;
    setExportStage(enq.data.status === "processing" ? "processing" : "queued");

    // 2. Poll
    const started = Date.now();
    const maxMs = 300000; // 5-minute client cap — renders can take 2-3 minutes
    let status = enq.data.status;
    while (status !== "ready" && status !== "failed") {
      if (Date.now() - started > maxMs) {
        const err = new Error("Your deck is still rendering in the background.");
        err.stillProcessing = true;
        throw err;
      }
      await new Promise((r) => setTimeout(r, pollMs));
      const s = await api.get(`/pitch/decks/${deckId}/export/status/${jobId}`);
      status = s.data.status;
      // Lift the REAL job status into state so the progress donut advances
      // through actual stages instead of a fake time curve.
      if (status === "queued" || status === "processing") setExportStage(status);
      if (status === "failed") {
        throw new Error(s.data.error || "PPTX generation failed. Your credits have been refunded.");
      }
    }

    // 3. Download the blob
    const dl = await api.get(
      `/pitch/decks/${deckId}/export/download/${jobId}`,
      { responseType: "blob" },
    );
    return dl.data;
  };

  const createPitchDeck = async () => {
    // Confirm with the user — this is the costly action (25 credits)
    if (!window.confirm("This will use 25 credits to generate your branded PowerPoint deck. Continue?")) return;
    setCreating(true);
    setExportDone(false);
    setExportStage("queued");
    try {
      const blob = await runAsyncExport();
      setExportDone(true);
      await new Promise((r) => setTimeout(r, 700));
      setCreatedBlob(blob);
      // Reflect the paid export locally so the free-PDF button unlocks
      // immediately in the pre-download Play Deck.
      setDeck((d) => ({ ...d, exported_at: new Date().toISOString(), export_count: (d?.export_count || 0) + 1 }));
      window.dispatchEvent(new Event("clinq:credits-changed"));
      toast.success("Your pitch deck is ready · 25 credits used", {
        description: "A copy has been emailed to you and saved permanently under My Exports on your dashboard.",
        duration: 9000,
      });
    } catch (e) {
      // Client-side timeout is NOT a failure — the job keeps rendering on the
      // server. On completion the file lands in My Exports + the user's inbox;
      // if the job actually fails, credits are auto-refunded server-side.
      if (e?.stillProcessing) {
        toast.info("Your deck is still rendering — we'll finish it in the background", {
          description: "The finished file will appear under My Exports on your dashboard and be emailed to you. Credits are only kept if it completes — failures are refunded automatically.",
          duration: 15000,
          action: { label: "My Exports", onClick: () => navigate("/dashboard") },
        });
        return;
      }
      // Async flow throws either a shaped axios error (from enqueue POST) OR a
      // plain Error (from the polling/download stage). Route both cleanly.
      if (e?.response) {
        // `data` may be a Blob (responseType='blob' on download) OR JSON (from enqueue POST).
        // In both cases we need the inner `.detail` (an object with code/message).
        let detail;
        const raw = e?.response?.data;
        if (raw instanceof Blob) {
          try { detail = JSON.parse(await raw.text())?.detail; } catch { detail = null; }
        } else {
          detail = raw?.detail;
        }
        const shapedErr = {
          response: { status: e.response.status, headers: e.response.headers, data: { detail } },
          config: e.config,
        };
        handleCreditOrError(shapedErr, "Generation failed", { action: "create_pptx_deck" });
      } else {
        toast.error(e?.message || "Generation failed");
      }
    } finally {
      setCreating(false);
    }
  };

  // Centralised 402 handler — surface a clear "buy credits" CTA so the user
  // doesn't have to hunt for the topup page. For non-402 errors, adds a
  // "Copy details" action so founders can escalate cleanly to support.
  const handleCreditOrError = (e, fallback, ctx = {}) => {
    const d = e?.response?.data?.detail;
    const is402 =
      e?.response?.status === 402 &&
      d &&
      typeof d === "object" &&
      d.code === "INSUFFICIENT_CREDITS";
    toastApiError(e, fallback, {
      context: { deck_id: deckId, ...ctx },
      onBuyCredits: () => navigate("/credits"),
    });
    return is402;
  };

  const onIndustryChanged = (resp) => {
    setDeck((d) => ({
      ...d,
      industry: resp.industry,
      industry_other_desc: resp.industry_other_desc,
      industry_flagged_slides: resp.industry_flagged_slides || [],
    }));
    const flagged = resp.newly_flagged_slides || [];
    if (flagged.length) {
      const names = flagged.map((n) => deck?.slides?.find((s) => s.num === n)?.title || `Slide ${n}`);
      toast.warning(`New template applied — slides with new mandatory requirements: ${names.join(", ")}`, { duration: 10000 });
    } else {
      toast.success("Industry template updated");
    }
  };

  const dismissIndustryFlag = async (slideNum) => {
    try {
      await api.post(`/pitch/decks/${deckId}/industry/flags/dismiss`, { slide_num: slideNum });
      setDeck((d) => ({ ...d, industry_flagged_slides: (d.industry_flagged_slides || []).filter((n) => n !== slideNum) }));
    } catch {
      toast.error("Could not dismiss — please try again");
    }
  };

  const downloadCreated = () => {
    if (!createdBlob) return;
    const url = window.URL.createObjectURL(new Blob([createdBlob]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(deck.company_name || "deck").replace(/\s+/g, "_")}_pitch_deck.pptx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const runFailureCheck = async () => {
    try {
      const { data } = await api.get(`/pitch/decks/${deckId}/failure-check`);
      setCheck(data);
      setView("check");
    } catch {
      toast.error("Check failed");
    }
  };

  const exportDeck = async () => {
    try {
      const blob = await runAsyncExport();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(deck.company_name || "deck").replace(/\s+/g, "_")}_pitch_deck.pptx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Deck exported", {
        description: "A copy has been emailed to you and saved permanently under My Exports on your dashboard.",
        duration: 9000,
      });
    } catch (e) {
      if (e?.stillProcessing) {
        toast.info("Your deck is still rendering — we'll finish it in the background", {
          description: "The finished file will appear under My Exports on your dashboard and be emailed to you. Credits are only kept if it completes — failures are refunded automatically.",
          duration: 15000,
          action: { label: "My Exports", onClick: () => navigate("/dashboard") },
        });
        return;
      }
      toast.error(e?.message || "Export failed");
    }
  };

  if (!deck || !meta) {
    return <div className="min-h-screen text-white grid place-items-center" style={{ backgroundColor: C.bgDark }}>Loading…</div>;
  }

  const slideMeta = meta.slides.find((s) => s.num === activeSlide);
  const indReqs = (meta.industries.find((i) => i.id === deck.industry)?.mandatory || []).filter((m) => m.slide === activeSlide);
  const tplReqs = (meta.industry_templates?.[deck.industry]?.slide_requirements?.[String(activeSlide)]) || [];
  const industryFlagged = (deck.industry_flagged_slides || []).includes(activeSlide);
  const pillarScores = deck.pillar_scores || {};
  const errors = findings.filter((f) => f.severity === "error");
  const warns = findings.filter((f) => f.severity === "warn");

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: C.bgDark, fontFamily: "Manrope, sans-serif" }} data-testid="pitch-editor">
      {/* Top bar */}
      <header style={{ backgroundColor: "#000" }}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-shrink">
            <Link to="/dashboard" className="text-white/70 hover:text-white" data-testid="dashboard-link"><ArrowLeft size={18} /></Link>
            <ClinqLogo />
            <div className="hidden md:flex items-center gap-2 pl-4 border-l border-white/10 min-w-0">
              <span className="font-bold text-sm truncate" data-testid="deck-name">{deck.company_name}</span>
              <span className="text-xs text-white/40">·</span>
              <IndustryBadge deck={deck} meta={meta} onChanged={onIndustryChanged} />
              <span className="text-xs text-white/40">·</span>
              <span className="text-xs text-white/55">{(meta.funder_types.find((f) => f.id === deck.funder_type) || {}).name}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <CreditBalance variant="dark" />
            <span className="h-5 w-px bg-white/10 mx-1" aria-hidden="true" />
            <button onClick={() => setAdvisorOpen(true)} data-testid="open-advisor"
              className="text-xs font-bold px-3 py-2 rounded-md inline-flex items-center gap-1.5 transition-colors"
              style={{ backgroundColor: "rgba(245, 158, 11, 0.12)", color: C.orange, border: `1px solid ${C.orange}55` }}
              title="Book a 30-min call with a Clinq advisor to walk through your deck.">
              <ChatCircleText size={14} weight="fill" /> Talk to an advisor
            </button>
            <button onClick={() => setTutorialOpen(true)} data-testid="open-tutorial"
              className="text-xs font-semibold px-3 py-2 rounded-md inline-flex items-center gap-1.5 hover:bg-white/5 transition-colors"
              style={{ color: C.tealSoft, border: "1px solid rgba(123, 211, 206, 0.3)" }}
              title="A 5-step explainer on how the Clinq pitch deck builder works.">
              <BookOpen size={14} weight="fill" /> How this works
            </button>
            <span className="h-5 w-px bg-white/10 mx-1" aria-hidden="true" />
            <input ref={fileInputRef} type="file" accept=".pptx,.pdf" data-testid="import-file-input"
              onChange={(e) => importDeck(e.target.files?.[0])} style={{ display: "none" }} />
            <button onClick={() => fileInputRef.current?.click()} disabled={importing} data-testid="import-deck-btn"
              className="relative text-xs font-semibold px-3 py-2 rounded-md inline-flex items-center gap-1.5 hover:bg-white/5 transition-colors disabled:opacity-80 overflow-hidden"
              title="Upload an existing .pptx or .pdf (up to 40 MB) — costs 5 credits. Claude maps it into the 11-slide methodology and saves overflow content as sub-slides.">
              {importing && importPhase === "uploading" && (
                <span aria-hidden="true" className="absolute inset-y-0 left-0 transition-all duration-150"
                  style={{ width: `${importPct}%`, backgroundColor: "rgba(10, 139, 133, 0.25)" }} />
              )}
              <UploadSimple size={14} className="relative" />
              <span className="relative" data-testid="import-deck-btn-label">
                {!importing && "Upload existing deck"}
                {importing && importPhase === "uploading" && `Uploading ${importPct}%`}
                {importing && importPhase === "processing" && "Processing with AI…"}
                {importing && importPhase === "idle" && "Importing…"}
              </span>
            </button>
            <button onClick={() => setShareOpen(true)} data-testid="open-share"
              className="text-xs font-semibold px-3 py-2 rounded-md inline-flex items-center gap-1.5 hover:bg-white/5 transition-colors"
              title="Share a read-only link to this deck. Anyone with the link can view it — no login needed.">
              <Share size={14} /> Share
            </button>
            <button onClick={createPitchDeck} disabled={creating} data-testid="create-deck-btn"
              className="text-xs font-bold px-4 py-2 rounded-md inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
              style={{ backgroundColor: C.teal, color: "#fff" }}
              title="Generates a branded PowerPoint deck with your logo, colour, content and images — ready to view or download.">
              <MagicWand size={14} weight="fill" /> {creating ? "Generating…" : "Create your pitch deck"}
            </button>
          </div>
        </div>
        {/* Download previous exports — expandable panel under the create button */}
        <div className="max-w-[1800px] mx-auto px-4 flex justify-end">
          <button onClick={() => setPrevExportsOpen((o) => !o)} data-testid="toggle-previous-exports"
            className="text-[11px] font-semibold inline-flex items-center gap-1.5 py-1.5 px-2 rounded-md hover:bg-white/5 transition-colors"
            style={{ color: C.tealSoft }}
            title="Every deck you've generated is saved permanently — download any previous version here.">
            <ClockCounterClockwise size={12} weight="bold" />
            Download previous exports
            <CaretDown size={10} weight="bold"
              style={{ transform: prevExportsOpen ? "rotate(180deg)" : "none", transition: "transform 150ms" }} />
          </button>
        </div>
        {prevExportsOpen && (
          <div className="max-w-[1800px] mx-auto px-4 pb-3" data-testid="previous-exports-panel">
            <MyExports compact />
          </div>
        )}
      </header>

      {view === "slides" && (
        <>
        <ContinueOnDesktopNotice surface="the deck editor" />
        {(deck.flow_version === "wizard") && (
          <WizardChrome
            deck={deck}
            view="slides"
            activeSlide={activeSlide}
            activeExtraId={activeExtraId}
            onGoto={switchSlide}
            onGotoBrand={() => setView("brand")}
            onGotoCheck={async () => { setView("check"); await runFailureCheck(); }}
            onGotoGenerate={() => setView("generate")}
            data-testid="wizard-chrome"
          />
        )}
        <div className={`max-w-[1400px] mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 gap-6 ${
          deck.flow_version === "wizard" ? "" : "lg:grid-cols-12"
        }`}>
          {/* Fix-It hint — carried over from a failed funder-ready check */}
          {fixHint && fixHint.slide_num === activeSlide && !activeExtraId && (
            <div className={deck.flow_version === "wizard" ? "" : "lg:col-span-12"}>
              <div data-testid="fix-hint-banner"
                className="flex items-start gap-3 p-4 rounded-lg"
                style={{ backgroundColor: "rgba(245,158,11,0.1)", border: `1px solid ${C.orange}` }}>
                <XCircle size={18} weight="fill" style={{ color: C.orange, marginTop: 2, flexShrink: 0 }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: C.orange }}>
                    Fix this to pass {fixHint.code.split("_")[0]} · funder-ready check
                  </div>
                  <div className="text-sm text-white/85 mt-1">{fixHint.reason}</div>
                </div>
                <button onClick={() => { setFixHint(null); setView("check"); runFailureCheck(); }}
                  data-testid="fix-hint-recheck-btn"
                  className="flex-shrink-0 text-[11px] font-bold px-3 py-1.5 rounded-md"
                  style={{ backgroundColor: C.teal, color: "#fff" }}>
                  Re-run check
                </button>
                <button onClick={() => setFixHint(null)} data-testid="fix-hint-dismiss-btn"
                  aria-label="Dismiss fix hint"
                  className="flex-shrink-0 p-1 rounded text-white/50 hover:text-white">
                  <X size={14} weight="bold" />
                </button>
              </div>
            </div>
          )}
          {/* Left rail — slide list + pillar scores (hidden in wizard mode) */}
          {deck.flow_version !== "wizard" && (
          <aside className="lg:col-span-3">
            <AdvisorSessionsInline compact />
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] mb-3 mt-4" style={{ color: C.orange }}>
              {deck.slides.filter((s) => s.num !== 7).length} slides + extras
            </div>
            <div className="space-y-1" data-testid="slide-list">
              {deck.slides.filter((s) => s.num !== 7).map((s) => {
                const isActive = s.num === activeSlide && !activeExtraId;
                const filled = (s.content || "").trim().length > 20;
                const extras = s.extras || [];
                return (
                  <div key={s.num}>
                    <button onClick={() => switchSlide(s.num)}
                      data-testid={`slide-tab-${s.num}`}
                      className="w-full text-left px-3 py-2.5 rounded-md transition-colors flex items-center justify-between gap-2"
                      style={{
                        backgroundColor: isActive ? "rgba(44, 197, 168, 0.12)" : "transparent",
                        borderLeft: isActive ? `2px solid ${C.teal}` : "2px solid transparent",
                      }}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[10px] text-white/40">/{String(s.num).padStart(2, "0")}</span>
                          <span className={`text-sm truncate ${isActive ? "font-bold" : "font-medium text-white/80"}`}>{s.title}</span>
                        </div>
                        {s.pillar && <div className="text-[9px] uppercase tracking-widest mt-0.5 text-white/40">{s.pillar}</div>}
                      </div>
                      {filled ? <CheckCircle size={12} weight="fill" style={{ color: C.teal }} />
                              : <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "rgba(255,255,255,0.15)" }}></span>}
                    </button>
                    {/* Extras under this slide */}
                    {extras.map((ex) => {
                      const isExActive = s.num === activeSlide && activeExtraId === ex.id;
                      const exFilled = (ex.content || "").trim().length > 20;
                      return (
                        <div key={ex.id} className="ml-4 flex items-center gap-1">
                          <button onClick={() => switchSlide(s.num, ex.id)}
                            data-testid={`slide-extra-${ex.id}`}
                            className="flex-1 text-left px-3 py-1.5 rounded-md transition-colors flex items-center justify-between gap-2 text-xs"
                            style={{
                              backgroundColor: isExActive ? "rgba(44, 197, 168, 0.08)" : "transparent",
                              borderLeft: isExActive ? `2px solid ${C.tealSoft}` : "2px solid transparent",
                            }}>
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="font-mono text-[9px]" style={{ color: C.tealSoft }}>↳</span>
                              <span className={`truncate ${isExActive ? "font-bold text-white" : "text-white/65"}`}>{ex.title || "Sub-slide"}</span>
                            </div>
                            {exFilled && <CheckCircle size={10} weight="fill" style={{ color: C.teal }} />}
                          </button>
                          <button onClick={(e) => deleteExtra(s.num, ex.id, e)}
                            data-testid={`delete-extra-${ex.id}`}
                            className="text-white/30 hover:text-orange-400 px-1 transition-colors"
                            title="Delete sub-slide">
                            <Trash size={11} />
                          </button>
                        </div>
                      );
                    })}
                    {/* + Add sub-slide.
                        Iter86 rules:
                          • slide 5 (Business Model): hidden always — locked single-slide.
                          • slide 9 (Team): hidden unless team has >4 members
                            (i.e., there is real overflow to justify a
                            second slide). Founders with a small team
                            no longer see a stray "Add sub-slide" CTA. */}
                    {isActive && s.num !== 5 && !(s.num === 9 && (deck.team || []).length <= 4) && (
                      <button onClick={() => addExtra(s.num)}
                        data-testid={`add-extra-${s.num}`}
                        className="ml-4 mt-1 mb-1 text-[10px] uppercase tracking-widest font-semibold text-white/45 hover:text-[#7BD3CE] transition-colors inline-flex items-center gap-1 px-3 py-1">
                        <Plus size={10} weight="bold" /> Add sub-slide
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pillar score panel */}
            <div className="mt-8 p-4 rounded-xl border" style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.15)" }}>
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] mb-3" style={{ color: C.tealSoft }}>
                Readiness · 5 pillars
              </div>
              {["P1", "P2", "P3", "P4", "P5"].map((p) => (
                <div key={p} className="mb-3 last:mb-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-white/70">{p} · {meta.pillars[p]}</span>
                    <span className="font-mono text-xs font-bold" style={{ color: (pillarScores[p] ?? 100) > 60 ? C.teal : C.orange }}>
                      {pillarScores[p] ?? 100}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
                    <div className="h-full transition-all" style={{
                      width: `${pillarScores[p] ?? 100}%`,
                      backgroundColor: (pillarScores[p] ?? 100) > 60 ? C.teal : C.orange,
                    }}></div>
                  </div>
                </div>
              ))}
            </div>
          </aside>
          )}

          {/* Editor */}
          <main className={`${deck.flow_version === "wizard" ? "" : "lg:col-span-9"} min-w-0`}>
            {predraftOffer}
            <FactsQueuePanel deck={deck} onUpdated={(data) => {
              setDeck((d) => ({ ...d, slides: (data.slides || []).filter((s) => s && s.num !== 7), facts_queue: data.facts_queue }));
              setContent((prev) => {
                const cur = (data.slides || []).find((s) => s.num === activeSlide);
                return activeExtraId ? prev : (cur?.content ?? prev);
              });
            }} />
            {!activeExtraId && (
              <ReviewPanel deck={deck} slideNum={activeSlide} onUpdated={(data) => {
                setDeck((d) => ({ ...d,
                  slides: data.slides ? data.slides.filter((s) => s && s.num !== 7) : d.slides,
                  review_state: data.review_state }));
                if (data.slides) {
                  setContent((prev) => {
                    const cur = data.slides.find((s) => s.num === activeSlide);
                    return cur ? (cur.content ?? prev) : prev;
                  });
                }
              }} />
            )}
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-[10px] font-mono tracking-[0.25em]" style={{ color: C.orange }}>
                / {String(activeSlide).padStart(2, "0")} {slideMeta?.pillar && `· ${slideMeta.pillar} ${meta.pillars[slideMeta.pillar]}`}
                {activeExtraId && <span className="text-white/55"> · sub-slide</span>}
              </div>
              <div className="text-xs text-white/40" data-testid="save-status">{saving ? "Saving…" : "Saved"}</div>
            </div>
            <h1 className="font-extrabold text-4xl tracking-tight" style={{ letterSpacing: "-0.02em" }}>
              {activeExtraId
                ? (deck.slides.find((s) => s.num === activeSlide)?.extras || []).find((x) => x.id === activeExtraId)?.title || "Sub-slide"
                : slideMeta?.title}
            </h1>
            <div className="text-sm italic text-white/55 mt-2">{slideMeta?.question}</div>

            {indReqs.length > 0 && (
              <div className="mt-5 p-4 rounded-lg border" style={{ backgroundColor: "rgba(245, 158, 11, 0.08)", borderColor: "rgba(245, 158, 11, 0.3)" }}>
                <div className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: C.orange }}>
                  Industry mandate — required for {deck.industry.replace("_", " ")}
                </div>
                {indReqs.map((r) => (
                  <div key={r.key} className="text-sm text-white/80">• {r.label}</div>
                ))}
              </div>
            )}

            {tplReqs.length > 0 && !activeExtraId && (
              <div className="mt-4 p-4 rounded-lg border" data-testid="industry-template-reqs"
                style={{ backgroundColor: "rgba(10, 139, 133, 0.08)", borderColor: "rgba(10, 139, 133, 0.35)" }}>
                <div className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: C.tealSoft }}>
                  {meta.industry_templates?.[deck.industry]?.name} template — this slide must include
                </div>
                {tplReqs.map((r, i) => (
                  <div key={i} className="text-[13px] text-white/80 mt-1 flex items-start gap-2">
                    <span className="mt-1.5 inline-block w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: C.tealSoft }} />
                    <span>{r}</span>
                  </div>
                ))}
              </div>
            )}

            {industryFlagged && !activeExtraId && (
              <div className="mt-4 p-4 rounded-lg border flex items-start justify-between gap-4" data-testid="industry-flag-banner"
                style={{ backgroundColor: "rgba(245, 158, 11, 0.1)", borderColor: "rgba(245, 158, 11, 0.4)" }}>
                <div className="text-[13px] text-white/85">
                  <span className="font-bold" style={{ color: C.orange }}>New template requirement.</span>{" "}
                  Your switch to the {meta.industry_templates?.[deck.industry]?.name} template added mandatory content to this slide — review the must-haves above and update your content.
                </div>
                <button onClick={() => dismissIndustryFlag(activeSlide)} data-testid="industry-flag-dismiss"
                  className="text-xs font-bold shrink-0 px-2.5 py-1 rounded-md"
                  style={{ backgroundColor: "rgba(245,158,11,0.2)", color: C.orange }}>
                  Got it
                </button>
              </div>
            )}

            {/* Slide 10 = Financial Projections — gentle nudge to the model builder */}
            {activeSlide === 10 && !activeExtraId && (
              <div className="mt-5 rounded-lg border px-4 py-3 flex items-start gap-3"
                style={{ borderColor: "rgba(245, 158, 11, 0.3)", backgroundColor: "rgba(245, 158, 11, 0.06)" }}
                data-testid="slide10-model-hint">
                <Sparkle size={14} weight="fill" style={{ color: C.orange }} className="mt-0.5 shrink-0" />
                <div className="text-[13px] leading-relaxed flex-1">
                  <span className="font-bold text-white/90">Don&apos;t have financial projections yet?</span>{" "}
                  <span className="text-white/70">No worries — Clinq builds you a stage-appropriate 3+5-year model in about 15 minutes. </span>
                  <Link to="/fm/new" target="_blank" rel="noopener" data-testid="slide10-model-link"
                    className="font-bold underline-offset-2 hover:underline"
                    style={{ color: C.tealSoft }}>
                    Click here to build your financial model →
                  </Link>
                </div>
              </div>
            )}

            {/* Slide 9 = Team — structured roster instead of free-text */}
            {activeSlide === 9 && !activeExtraId ? (
              <InlineTeam deckId={deckId} team={deck.team || []} onSaved={(t) => setDeck((d) => ({ ...d, team: t }))} />
            ) :
            /* Slide 10 = Financial Projections — structured table instead of free-text */
            activeSlide === 10 && !activeExtraId ? (
              <InlineProjections deckId={deckId} projections={deck.projections} onSaved={(p) => setDeck((d) => ({ ...d, projections: p }))} />
            ) :
            /* Slide 12 = Thank You — capture contact info instead of free-text */
            activeSlide === 12 && !activeExtraId ? (
              <InlineClosing deckId={deckId} closing={deck.closing} onSaved={(c) => setDeck((d) => ({ ...d, closing: c }))} />
            ) : (
              <>
              <div className="mt-6">
              {/* Header row (label + AI draft button + notes input).
                  Iter59: hidden on slide 11 per user spec — the Capital
                  Ask form owns its own header, and there is intentionally
                  no AI draft on this slide.
                  Iter63: also hidden on slide 1 (Cover & Snapshot) — the
                  new cover form replaces the free-text content area.
                  Iter86: also hidden on slide 4 (Market Size) and slide 5
                  (Business Model) — those slides own dedicated forms
                  (MarketSizeForm / BusinessModelForm) with per-field AI
                  redraft, so a top-level "AI draft" button would double
                  up on affordances and confuse the founder. */}
              {!(([1, 4, 5, 11].includes(activeSlide)) && !activeExtraId) && (
              <div className="flex items-center justify-between mb-2"
                data-testid="slide-ai-draft-header">
                <div className="flex items-baseline gap-2 min-w-0">
                  <label className="text-xs uppercase tracking-widest text-white/50 font-semibold whitespace-nowrap">
                    {activeSlide === 8 && !activeExtraId
                      ? "Slide content (comparison matrix)"
                      : "Slide content (markdown bullets)"}
                  </label>
                  {/* Iter90: live bullet counter — same nudge pattern as
                      iter89's team-bio counter. Founders often paste 6-8
                      bullets and don't realise funders only see the
                      first 5 (OVERFLOW_THRESHOLD). Colour transitions
                      at the threshold + at 2× threshold.
                      Hidden on slide 8 — the comparison matrix renders
                      all rows (up to 8), so the 5-bullet overflow nudge
                      would be inaccurate there. */}
                  {!(activeSlide === 8 && !activeExtraId) && (() => {
                    const n = bulletCount;
                    const cap = OVERFLOW_THRESHOLD;
                    const tone =
                      n === 0 ? "text-white/30"
                        : n <= cap ? "text-white/50"
                          : n <= cap * 2 ? "text-amber-400"
                            : "text-red-400";
                    const hint =
                      n === 0 ? "Add up to 5 punchy bullets"
                        : n <= cap ? `${n} / ${cap} · funders see all`
                          : `${n} bullets · funders see the first ${cap} — spill to sub-slide?`;
                    const shortHint =
                      n === 0 ? `0 / ${cap}`
                        : n <= cap ? `${n} / ${cap}`
                          : `${n} / ${cap}`;
                    return (
                      <span
                        data-testid="slide-bullet-counter"
                        className={`text-[10px] font-mono tabular-nums ${tone} truncate`}
                        title={hint}
                      >
                        <span className="sm:hidden">{shortHint}</span>
                        <span className="hidden sm:inline">· {hint}</span>
                      </span>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-2">
                  <input type="text" value={extraNotes} onChange={(e) => setExtraNotes(e.target.value)}
                    placeholder="Notes for AI (optional): metrics, names, sources…"
                    data-testid="ai-notes-input"
                    className="text-xs px-3 py-1.5 rounded-md w-72 placeholder:text-white/30"
                    style={{ backgroundColor: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
                  <button onClick={draft} disabled={drafting} data-testid="ai-draft-btn"
                    className="text-xs font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 disabled:opacity-60 transition-colors"
                    style={{ backgroundColor: C.orange, color: "#000" }}
                    title="Uses Claude AI to draft this slide based on your business context (industry, stage, funder type). Output is methodology-compliant: specific numbers, cited sources, no marketing fluff.">
                    <Sparkle size={12} weight="fill" /> {drafting ? "Drafting…" : "AI draft"}
                  </button>
                  {/* Iter102: inline draft progress indicator — cycles
                      through plausible AI phases every 1.6s so the 6-12s
                      wait feels dynamic instead of a frozen button. */}
                  {drafting && (
                    <PhaseIndicator
                      phases={AI_DRAFT_PHASES}
                      testid="draft-phase-indicator"
                    />
                  )}
                  {activeSlide === 11 && !activeExtraId && (
                    <div className="inline-flex items-center gap-2">
                      {userModels.length > 1 && (
                        <select value={selectedModelId}
                          onChange={(e) => setSelectedModelId(e.target.value)}
                          data-testid="funding-model-picker"
                          className="text-xs px-2 py-1.5 rounded-md max-w-[180px] truncate focus:outline-none"
                          style={{ backgroundColor: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }}
                          title="Choose which financial model to import from">
                          {userModels.map((m) => (
                            <option key={m.model_id} value={m.model_id} style={{ backgroundColor: "#0A2826" }}>
                              {m.name || m.profile?.company_name || m.model_id}
                            </option>
                          ))}
                        </select>
                      )}
                      <button onClick={insertFundingPlan}
                        disabled={userModels.length === 0}
                        data-testid="import-funding-plan-btn"
                        className="text-xs font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors disabled:opacity-40"
                        style={{ border: `1px solid ${C.tealSoft}55`, color: C.tealSoft }}
                        title={userModels.length === 0
                          ? "Build a financial model first to enable this import."
                          : "Pulls capital required, debt/equity mix, debt terms and dilution from the selected financial model and inserts it as bullets."}>
                        <DownloadSimple size={12} weight="bold" />
                        {userModels.length === 0 ? "No model to import" : "Import from financial model"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              )}
              {!((activeSlide === 11 || activeSlide === 1) && !activeExtraId) && (
              <div className="text-[11px] text-white/45 mb-2 leading-relaxed flex items-start gap-1.5">
                <Info size={12} className="flex-shrink-0 mt-0.5" style={{ color: C.tealSoft }} />
                <span>
                  <span className="font-semibold" style={{ color: C.tealSoft }}>AI draft</span> writes this slide for you using your business context.
                  It cites real sources, uses present-tense facts, and refuses to invent numbers — gaps are marked <span className="font-mono text-white/55">[PLACEHOLDER: …]</span> so you can fill them in.
                </span>
              </div>
              )}
              {activeSlide === 1 && !activeExtraId && (
                <div className="text-[11px] mb-2 leading-relaxed flex items-start gap-1.5 p-2.5 rounded"
                  style={{ backgroundColor: "rgba(245, 158, 11, 0.08)", border: "1px solid rgba(245, 158, 11, 0.22)" }}
                  data-testid="cover-slide-note">
                  <Info size={12} className="flex-shrink-0 mt-0.5" style={{ color: C.orange }} />
                  <span className="text-white/70">
                    <span className="font-semibold" style={{ color: C.orange }}>Cover &amp; Snapshot tip:</span>{" "}
                    the top of the slide shows your <strong>company name</strong> + <strong>tagline</strong> (set in step 1 · Brand &amp; style).
                    The bullets you add below render as a <strong>Snapshot panel</strong> on the bottom half — keep them tight (max 4 bullets, each fits ~2 lines of the preview column). We&apos;ll flag any bullet that spills past two lines.
                  </span>
                </div>
              )}
              {(() => {
                // Iter51 — surface methodology-driven placeholder text on
                // FRESH parent slides so first-time founders know exactly
                // what to write. Falls back to the generic placeholder for
                // sub-slides, or when the founder has previously engaged
                // with the slide (already has content, already has notes,
                // or has a drafts_history from a prior AI attempt).
                const activeSlideObj = deck?.slides?.find((s) => s.num === activeSlide);
                const hasDraftHistory = ((activeSlideObj?.drafts_history) || []).length > 0;
                const isFresh =
                  !activeExtraId &&
                  (content || "").trim() === "" &&
                  (extraNotes || "").trim() === "" &&
                  !hasDraftHistory;
                const placeholder = isFresh
                  ? getSlidePlaceholder(activeSlide)
                  : GENERIC_PLACEHOLDER;
                // Slide 4 (Market Size) uses a structured 3-ring form
                // (iter53) instead of the free-form textarea. Data still
                // persists to `slide.content` in the shape parseMarketSize
                // understands so the preview/exporter/premium-rewrite flows
                // keep working without changes. Sub-slides of Market Size
                // still get the plain textarea.
                // Slide 1 (Cover & Snapshot) — 4 canonical bullets that
                // render at the bottom of the fixed cover layout (iter63).
                if (activeSlide === 1 && !activeExtraId) {
                  return (
                    <CoverSnapshotForm
                      content={content}
                      onChange={setContent}
                      primary={deck.brand_color || "#0A8B85"}
                    />
                  );
                }
                if (activeSlide === 4 && !activeExtraId) {
                  return (
                    <MarketSizeForm
                      content={content}
                      onChange={setContent}
                      primary={deck.brand_color || "#0A8B85"}
                    />
                  );
                }
                // Slide 5 (Business Model) — canonical 5-row layout with a
                // dedicated form + per-field AI redraft (iter55). Same
                // architectural pattern as slide 4.
                if (activeSlide === 5 && !activeExtraId) {
                  return (
                    <BusinessModelForm
                      content={content}
                      onChange={setContent}
                      primary={deck.brand_color || "#0A8B85"}
                      deckId={deckId}
                    />
                  );
                }
                // Slide 8 (Competitive Landscape) — feature-comparison
                // matrix: up to 8 features × own column + 3 competitors,
                // takeaway with AI draft.
                if (activeSlide === 8 && !activeExtraId) {
                  return (
                    <CompetitiveForm
                      content={content}
                      onChange={setContent}
                      primary={deck.brand_color || "#0A8B85"}
                      deckId={deckId}
                      companyName={deck.company_name}
                    />
                  );
                }
                // Slide 11 (Capital Structure & The Ask) — canonical
                // two-card layout with amount + Use of Funds bullets.
                // No AI draft (per iter59 user spec).
                if (activeSlide === 11 && !activeExtraId) {
                  return (
                    <CapitalAskForm
                      content={content}
                      onChange={setContent}
                      primary={deck.brand_color || "#0A8B85"}
                      currency={(deck.projections || {}).currency}
                    />
                  );
                }
                return (
                  <textarea value={content} onChange={(e) => setContent(e.target.value)}
                    data-testid="slide-editor"
                    rows={14}
                    placeholder={placeholder}
                    className="w-full p-4 rounded-md font-mono text-sm leading-relaxed focus:outline-none focus:border-[#0A8B85]"
                    style={{ backgroundColor: C.panel, border: "1px solid rgba(255,255,255,0.1)", color: "#fff", resize: "vertical" }} />
                );
              })()}
              {!activeExtraId && !LOCKED_LAYOUT_SLIDES.has(activeSlide) && (
                <OverflowBanner
                  deckId={deckId}
                  slideNum={activeSlide}
                  bulletCount={bulletCount}
                  threshold={OVERFLOW_THRESHOLD}
                  onSpilled={onOverflowSpilled}
                />
              )}
              {!activeExtraId && (
                <DraftHistoryPanel
                  deckId={deckId}
                  slideNum={activeSlide}
                  onRestored={({ content: newContent, findings: newFindings }) => {
                    setContent(newContent);
                    setFindings(newFindings || []);
                    setDeck((d) => ({
                      ...d,
                      slides: d.slides.map((s) => s.num === activeSlide ? { ...s, content: newContent } : s),
                    }));
                  }}
                />
              )}
              {/* Sub-slide jump chips — surfaced directly below the slide
                  content editor so founders can hop between the parent and
                  its overflow sub-slides without scrolling down to the
                  preview stack or up to the wizard nav bar. */}
              {(() => {
                const parentSlide = deck.slides.find((s) => s.num === activeSlide);
                const extras = (parentSlide?.extras) || [];
                if (!activeExtraId && extras.length === 0) return null;
                return (
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}
                       data-testid="content-subslide-jump">
                    <div className="text-[10px] uppercase tracking-widest text-white/45 font-semibold mb-2">
                      {activeExtraId ? "Also on this slide" : `Sub-slides on ${parentSlide?.title || "this slide"}`}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {activeExtraId && (
                        <button
                          type="button"
                          onClick={() => switchSlide(activeSlide)}
                          data-testid="content-jump-to-main"
                          className="text-[11px] font-bold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors hover:brightness-125"
                          style={{
                            backgroundColor: "rgba(10, 139, 133, 0.14)",
                            color: C.tealSoft,
                            border: "1px solid rgba(10, 139, 133, 0.32)",
                          }}
                          title={`Back to the main slide (${parentSlide?.title || `Slide ${activeSlide}`})`}
                        >
                          ← Main slide · {parentSlide?.title || `Slide ${activeSlide}`}
                        </button>
                      )}
                      {extras.map((ex) => {
                        const isActive = activeExtraId === ex.id;
                        return (
                          <button
                            key={ex.id}
                            type="button"
                            onClick={() => switchSlide(activeSlide, ex.id)}
                            disabled={isActive}
                            data-testid={`content-jump-to-extra-${ex.id}`}
                            className="text-[11px] font-bold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors hover:brightness-125 disabled:opacity-70 disabled:cursor-default"
                            style={{
                              backgroundColor: isActive ? "rgba(245, 158, 11, 0.24)" : "rgba(245, 158, 11, 0.10)",
                              color: C.orange,
                              border: `1px solid rgba(245, 158, 11, ${isActive ? "0.55" : "0.32"})`,
                            }}
                            title={isActive
                              ? "You're editing this sub-slide"
                              : `Open sub-slide: ${ex.title || "Sub-slide"}`}
                          >
                            ↳ {ex.title || "Sub-slide"}
                            {isActive && (
                              <span className="ml-1 text-[9px] uppercase tracking-widest opacity-70">Editing</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Photos for this slide (or the active sub-slide). Sub-slides
                get their own image gallery + auto-photo + regenerate flows
                so overflow content can illustrate itself just like any
                other slide.
                Iter63: hidden entirely on canonical no-photo slides
                (Market Size, Business Model, Capital Ask) since those
                layouts don't include any image slot. */}
            {!NO_PHOTOS_SLIDES.has(activeSlide) && (() => {
              const parentSlide = deck.slides.find((s) => s.num === activeSlide);
              const activeExtra = activeExtraId
                ? (parentSlide?.extras || []).find((x) => x.id === activeExtraId)
                : null;
              const imgs = activeExtraId ? (activeExtra?.images || []) : (parentSlide?.images || []);
              const hasAutoPhoto = imgs.some((im) => im?.source === "auto_generated");
              return (
              <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase tracking-widest text-white/50 font-semibold inline-flex items-center gap-1.5">
                    <ImageIcon size={12} /> Photos &amp; diagrams
                    {activeExtraId && (
                      <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: "rgba(245, 158, 11, 0.14)", color: C.orange, border: "1px solid rgba(245, 158, 11, 0.32)" }}>
                        Sub-slide
                      </span>
                    )}
                  </label>
                  <div className="flex items-center gap-2">
                    {/* Sub-slides don't have the Improve Design gallery,
                        so we surface the initial 3-credit generate button
                        right here. Once a photo exists, the Regenerate
                        button below overlays each image tile. */}
                    {activeExtraId && !hasAutoPhoto && imgs.length < 6 && (
                      <button onClick={generateSubSlidePhoto}
                        disabled={generatingSubPhoto}
                        data-testid="subslide-auto-photo-btn"
                        className="text-xs font-bold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
                        style={{ backgroundColor: "rgba(245, 158, 11, 0.14)", color: C.orange, border: "1px solid rgba(245, 158, 11, 0.35)" }}
                        title="Generate an AI photo for this sub-slide with Nano Banana — costs 3 credits">
                        <Sparkle size={11} weight="fill" />
                        {generatingSubPhoto ? "Generating…" : "Auto-generate · 3 cr"}
                      </button>
                    )}
                    {/* Iter103: rotating phase strings while Nano Banana
                        composes the image (~10-15s wait). */}
                    {generatingSubPhoto && (
                      <PhaseIndicator
                        phases={AI_PHOTO_GEN_PHASES}
                        intervalMs={2000}
                        testid="subslide-auto-photo-phase"
                      />
                    )}
                    {regenerating && !activeExtraId && (
                      <PhaseIndicator
                        phases={AI_PHOTO_GEN_PHASES}
                        intervalMs={2000}
                        testid="regenerate-photo-phase"
                      />
                    )}
                    <input ref={imageInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{ display: "none" }}
                      data-testid="slide-image-input" onChange={(e) => uploadSlideImage(e.target.files?.[0])} />
                    <button onClick={() => imageInputRef.current?.click()} data-testid="upload-slide-image-btn"
                      className="text-xs font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 hover:bg-white/10 transition-colors"
                      style={{ border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }}
                      title="Upload up to 6 photos or diagrams per slide. They appear next to your bullets in the generated deck.">
                      <Plus size={11} weight="bold" /> Add photo
                    </button>
                  </div>
                </div>
                {/* Per-slide suggestion: what photos/diagrams would work here?
                    Skipped on sub-slides — the suggestion is keyed to the
                    parent slide's role and would be misleading here. */}
                {!activeExtraId && (
                  <div className="rounded-lg px-3.5 py-3 mb-3 flex items-start gap-2.5"
                    style={{ backgroundColor: "rgba(245, 158, 11, 0.06)", border: `1px solid ${C.orange}33` }}
                    data-testid="slide-image-suggestion">
                    <Sparkle size={12} weight="fill" className="mt-0.5 shrink-0" style={{ color: C.orange }} />
                    <div className="text-[12px] leading-relaxed">
                      <span className="font-bold text-white/90">What photos &amp; diagrams would work on this slide?</span>{" "}
                      <span className="text-white/65">{IMAGE_SUGGESTIONS[activeSlide] || FALLBACK_IMAGE_HINT}</span>
                    </div>
                  </div>
                )}
                <div className="text-[11px] text-white/40 mb-3">PNG, JPG, WEBP or GIF · up to 3 MB · max 6 per slide</div>
                {imgs.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="slide-images-grid">
                    {imgs.map((im) => (
                      <div key={im.id} className="relative group rounded-lg overflow-hidden border" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                        <img src={im.url} alt={im.caption} className="w-full h-32 object-cover" />
                        {im.source === "auto_generated" && (
                          <div className="absolute top-2 left-2 flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest"
                            style={{ backgroundColor: "rgba(0,0,0,0.7)", color: C.orange, backdropFilter: "blur(4px)" }}
                            data-testid={`ai-badge-${im.id}`}
                            title="Auto-generated. Upload your own to replace, or regenerate for 1 credit.">
                            <Sparkle size={9} weight="fill" /> AI
                          </div>
                        )}
                        {im.source === "auto_generated" && (
                          <button onClick={regenerateSlidePhoto}
                            disabled={regenerating}
                            data-testid={`regenerate-photo-${im.id}`}
                            className="absolute bottom-2 left-2 right-2 py-1.5 rounded text-[10px] font-bold uppercase tracking-widest inline-flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-100"
                            style={{ backgroundColor: "rgba(245, 158, 11, 0.92)", color: "#1a1206" }}
                            title="Roll a fresh AI photo for 1 credit (you already paid 3 for the first one)">
                            <Sparkle size={10} weight="fill" />
                            {regenerating ? "Generating…" : "Regenerate · 1 cr"}
                          </button>
                        )}
                        <button onClick={() => deleteSlideImage(im.id)} data-testid={`delete-image-${im.id}`}
                          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center hover:bg-orange-500">
                          <Trash size={12} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg py-6 px-4 text-center text-xs text-white/40 border border-dashed" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                    {activeExtraId
                      ? "No photos yet. Auto-generate one, or upload your own image for this sub-slide."
                      : "No photos yet. Add product shots, team photos, charts or diagrams that go on this slide."}
                  </div>
                )}
              </div>
              );
            })()}

            {/* Findings */}
            {findings.length > 0 && (
              <div className="mt-5 p-4 rounded-lg" data-testid="findings"
                style={{ backgroundColor: C.panel, border: `1px solid rgba(255,255,255,0.08)` }}>
                <div className="text-[10px] font-bold uppercase tracking-widest mb-3"
                  style={{ color: errors.length ? C.orange : C.tealSoft }}>
                  {errors.length} error{errors.length !== 1 ? "s" : ""} · {warns.length} warning{warns.length !== 1 ? "s" : ""}
                </div>
                <div className="space-y-2">
                  {findings.map((f, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm" data-testid={`finding-${f.code}`}>
                      {f.severity === "error" ? <XCircle size={16} weight="fill" style={{ color: C.orange, marginTop: 2 }} />
                                              : <WarningCircle size={16} weight="fill" style={{ color: C.tealSoft, marginTop: 2 }} />}
                      <div className="flex-1">
                        <span className="font-mono text-[10px] uppercase text-white/40 mr-2">{f.code}</span>
                        <span className="text-white/85">{f.message}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {findings.length === 0 && content.trim() && !saving && (
              <div className="mt-5 p-4 rounded-lg flex items-center gap-2.5"
                style={{ backgroundColor: "rgba(44, 197, 168, 0.08)", border: `1px solid ${C.teal}` }}>
                <CheckCircle size={18} weight="fill" style={{ color: C.teal }} />
                <span className="text-sm">No issues — passes the standard funder checks for this slide.</span>
              </div>
            )}
            </>
            )}
            {/* Wizard footer — HTML preview of the current slide + Back/Next nav */}
            {deck.flow_version === "wizard" && !activeExtraId && (
              <WizardSlidePreviewAndNav
                deck={deck}
                setDeck={setDeck}
                slide={deck.slides.find((s) => s.num === activeSlide)}
                onOpenExtra={(extraId) => switchSlide(activeSlide, extraId)}
                onDeleteExtra={(extraId, e) => deleteExtra(activeSlide, extraId, e)}
                onUploadPhoto={(file) => {
                  // Accepts an optional File (drag-and-drop from the
                  // Picture-only dropzone). Falls back to opening the OS
                  // file picker when called with no arg (click path).
                  if (file instanceof File) uploadSlideImage(file);
                  else imageInputRef.current?.click();
                }}
                onBack={() => {
                  if (activeSlide > 1) {
                    // Symmetric with the forward-Next walk: if the previous
                    // slide has extras (sub-slides), land on its LAST extra
                    // rather than jumping over them. Forward Next was already
                    // walking parent → sub 1 → sub 2 → next parent, so Back
                    // now mirrors: next parent → sub 2 → sub 1 → parent.
                    // Iter56: skip slide 7 (GTM) both directions.
                    const prevNum = activeSlide === 8 ? 6 : activeSlide - 1;
                    const prev = deck.slides.find((s) => s.num === prevNum);
                    const prevExtras = (prev && prev.extras) || [];
                    if (prevExtras.length > 0) {
                      switchSlide(prevNum, prevExtras[prevExtras.length - 1].id);
                    } else {
                      switchSlide(prevNum);
                    }
                  } else {
                    setView("brand");  // Slide 1 → Brand (step 1)
                  }
                }}
                onNext={() => {
                  // Iter56: skip slide 7 (Go-to-Market) — 6 → 8.
                  const nextNum = activeSlide === 6 ? 8 : activeSlide + 1;
                  if (nextNum <= 12) switchSlide(nextNum);
                  else { setView("check"); runFailureCheck(); }  // Slide 12 → Funder-ready check
                }}
              />
            )}
            {/* Sub-slide footer nav — sub-slides skip the main preview+design
                gallery (that lives on the parent) but still need Back/Next so
                founders don't get stranded. Back walks previous extras → parent;
                Next walks next extras → next slide's main → funder-ready check. */}
            {deck.flow_version === "wizard" && activeExtraId && (() => {
              const parent = deck.slides.find((s) => s.num === activeSlide) || {};
              const extras = parent.extras || [];
              const idx = extras.findIndex((x) => x.id === activeExtraId);
              const parentTitle = parent.title || `Slide ${activeSlide}`;
              const prevExtra = idx > 0 ? extras[idx - 1] : null;
              const nextExtra = idx >= 0 && idx < extras.length - 1 ? extras[idx + 1] : null;
              const backLabel = prevExtra
                ? `Back: ↳ ${prevExtra.title || "Sub-slide"}`
                : `Back to ${parentTitle}`;
              const nextLabel = nextExtra
                ? `Next: ↳ ${nextExtra.title || "Sub-slide"}`
                : (activeSlide < 12
                    ? `Next: ${deck.slides.find((s) => s.num === (activeSlide === 6 ? 8 : activeSlide + 1))?.title || `Slide ${activeSlide + 1}`}`
                    : "Next: Funder-ready check");
              return (
                <WizardFooterNav
                  backLabel={backLabel}
                  nextLabel={nextLabel}
                  onBack={() => {
                    if (prevExtra) switchSlide(activeSlide, prevExtra.id);
                    else switchSlide(activeSlide);  // back to parent main slide
                  }}
                  onNext={() => {
                    if (nextExtra) switchSlide(activeSlide, nextExtra.id);
                    else if (activeSlide < 12) {
                      // Iter56: skip slide 7 (Go-to-Market) — 6 → 8.
                      const nextNum = activeSlide === 6 ? 8 : activeSlide + 1;
                      switchSlide(nextNum);
                    }
                    else { setView("check"); runFailureCheck(); }
                  }}
                />
              );
            })()}
          </main>
        </div>
        </>
      )}

      {view === "brand" && (
        <>
        <ContinueOnDesktopNotice surface="the brand panel" />
        {predraftOffer}
        {(deck.flow_version === "wizard") && (
          <WizardChrome
            deck={deck}
            view="brand"
            activeSlide={activeSlide}
            activeExtraId={activeExtraId}
            onGoto={(n, extraId) => { setView("slides"); switchSlide(n, extraId); }}
            onGotoBrand={() => setView("brand")}
            onGotoCheck={async () => { setView("check"); await runFailureCheck(); }}
            onGotoGenerate={() => setView("generate")}
          />
        )}
        <BrandView
          deck={deck}
          onBack={() => setView("slides")}
          onUploadLogo={() => logoInputRef.current?.click()}
          onRemoveLogo={removeLogo}
          onColorChange={updateBrandColor}
          logoInputRef={logoInputRef}
          onLogoFile={uploadLogo}
        />
        {(deck.flow_version === "wizard") && (
          <WizardFooterNav
            onBack={null}
            onNext={() => {
              setView("slides"); switchSlide(1);
              // Offer to import an existing deck as soon as the founder lands
              // on the first content slide — only once per deck, and only if
              // they haven't already started filling slides.
              try {
                const key = `clinq:import-prompt:${deckId}`;
                const dismissed = localStorage.getItem(key);
                const hasContent = (deck?.slides || []).some(
                  (s) => (s.content || "").trim().length > 20);
                if (!dismissed && !hasContent) {
                  setTimeout(() => setImportPromptOpen(true), 400);
                }
              } catch (_) { /* localStorage may be blocked */ }
            }}
            nextLabel="Next: Cover & Snapshot"
            showBack={false}
          />
        )}
        </>
      )}

      {view === "check" && (
        <>
        {(deck.flow_version === "wizard") && (
          <WizardChrome
            deck={deck}
            view="check"
            activeSlide={activeSlide}
            onGoto={(n) => { setView("slides"); switchSlide(n); }}
            onGotoBrand={() => setView("brand")}
            onGotoCheck={async () => { setView("check"); await runFailureCheck(); }}
            onGotoGenerate={() => setView("generate")}
          />
        )}
        <FailureCheckView check={check} onBack={() => setView("slides")} onExport={createPitchDeck}
          onFix={(c) => { setFixHint(c); setView("slides"); switchSlide(c.slide_num); }} />
        {(deck.flow_version === "wizard") && (
          <WizardFooterNav
            onBack={() => { setView("slides"); switchSlide(12); }}
            onNext={() => setView("generate")}
            nextLabel="Next: Generate deck"
          />
        )}
        </>
      )}

      {view === "generate" && (
        <>
        {(deck.flow_version === "wizard") && (
          <WizardChrome
            deck={deck}
            view="generate"
            activeSlide={activeSlide}
            activeExtraId={activeExtraId}
            onGoto={(n, extraId) => { setView("slides"); switchSlide(n, extraId); }}
            onGotoBrand={() => setView("brand")}
            onGotoCheck={async () => { setView("check"); await runFailureCheck(); }}
            onGotoGenerate={() => setView("generate")}
          />
        )}
        <WizardFinalStep
          deck={deck}
          onBack={() => setView("check")}
          onGenerate={createPitchDeck}
          onShare={() => setShareOpen(true)}
          creating={creating}
          onJumpToSlide={(n) => { setView("slides"); switchSlide(n); }}
        />
        </>
      )}

      {createdBlob && (
        // Iter105: auto-open Play Deck as the pre-download parity-check
        // step. Founder pages through every slide and clicks the amber
        // "Download .pptx" CTA in the top-bar (or the last-slide floating
        // CTA) once they're happy. The old CreatedDeckModal ("Your deck
        // is ready · Download") is still around as a fallback but no
        // longer shown by default — this pre-download preview lets us
        // catch any parity gaps between the play deck and .pptx before
        // the founder actually saves the file.
        <PlayDeckModal
          open
          deck={deck}
          onClose={() => setCreatedBlob(null)}
          onDownload={() => { downloadCreated(); setCreatedBlob(null); }}
          downloadLabel="Download .pptx"
          downloadTestid="pre-download-play-deck-download-btn"
        />
      )}

      {shareOpen && (
        <ShareModal deck={deck} deckId={deckId} onClose={() => setShareOpen(false)}
          onUpdate={(isPublic) => setDeck((d) => ({ ...d, is_public: isPublic }))} />
      )}

      <David />

      <PitchTutorial
        open={tutorialOpen}
        deckId={deckId}
        onClose={() => setTutorialOpen(false)}
        onTalkToAdvisor={() => setAdvisorOpen(true)}
      />
      {advisorOpen && <AdvisorModal service="pitch_deck" onClose={() => setAdvisorOpen(false)} />}
      {importPromptOpen && (
        <ImportPromptModal
          onUpload={() => {
            setImportPromptOpen(false);
            try { localStorage.setItem(`clinq:import-prompt:${deckId}`, "used"); } catch (_) { /* noop */ }
            fileInputRef.current?.click();
          }}
          onDecline={() => {
            setImportPromptOpen(false);
            try { localStorage.setItem(`clinq:import-prompt:${deckId}`, "dismissed"); } catch (_) { /* noop */ }
          }}
        />
      )}
      {importing && <ImportProgressModal phase={importPhase} pct={importPct} />}
      <ExportProgressOverlay active={creating} done={exportDone} stage={exportStage}
        title="Building your PowerPoint"
        subtitle="Rendering every slide in high fidelity — most decks land in about a minute."
        longWaitSubtitle="Still working — larger decks can take 2–3 minutes. We'll also email it to you and save it to My Exports."
        testid="pptx-export-progress" />
      <LowCreditNudge />
    </div>
  );
}


/* Full-screen pie-chart progress ring shown while an existing pitch deck is
   uploading + being AI-mapped into the 11-slide methodology. Two phases:
     - "uploading" — deterministic ring fills 0-100% based on bytes sent.
     - "processing" — the ring keeps filling on a simulated curve (fast start,
       easing toward 92%) since the AI runs as one ~30-60s call with no
       backend stream; it snaps away when the job completes.
   Blocks the entire editor so the founder can't fire duplicate uploads. */
function ImportProgressModal({ phase, pct }) {
  // Ring geometry — pie chart drawn as a circle with a large stroke-width.
  const R = 62;
  const CIRC = 2 * Math.PI * R;
  const isProcessing = phase === "processing";
  const simPct = useSimulatedProgress(isProcessing, { cap: 92, tau: 15000 });
  const clamped = Math.max(0, Math.min(100, isProcessing ? simPct : (pct || 0)));
  const dashOffset = CIRC * (1 - clamped / 100);

  const headline = isProcessing
    ? "AI is mapping your deck"
    : "Uploading your pitch deck";
  const staticSubline = `${clamped}% uploaded`;

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center px-4"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.82)", backdropFilter: "blur(4px)" }}
      data-testid="import-progress-modal"
      aria-live="polite" aria-busy="true">
      <div className="rounded-3xl max-w-sm w-full p-8 relative border text-center"
        style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.22)" }}>
        <div className="mx-auto relative" style={{ width: 160, height: 160 }}>
          <svg width="160" height="160" viewBox="0 0 160 160">
            {/* Track */}
            <circle cx="80" cy="80" r={R}
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="14" />
            {/* Progress arc */}
            <circle cx="80" cy="80" r={R}
              fill="none"
              stroke={C.teal}
              strokeWidth="14"
              strokeLinecap="round"
              strokeDasharray={CIRC}
              strokeDashoffset={dashOffset}
              style={{
                transform: "rotate(-90deg)",
                transformOrigin: "50% 50%",
                transition: "stroke-dashoffset 300ms ease-out",
              }} />
          </svg>
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-3xl font-extrabold font-mono-num tracking-tight" style={{ color: C.tealSoft, letterSpacing: "-0.02em" }}
              data-testid="import-progress-pct">
              {clamped}%
            </div>
          </div>
        </div>
        <div className="mt-6 text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: C.tealSoft }}>
          {isProcessing ? "Analysing" : "Uploading"}
        </div>
        <h3 className="font-extrabold text-xl mt-1 tracking-tight" style={{ letterSpacing: "-0.02em" }}
          data-testid="import-progress-headline">
          {headline}
        </h3>
        <p className="text-white/60 mt-2 text-[13px] leading-relaxed" data-testid="import-progress-subline">
          {isProcessing ? (
            <PhaseIndicator
              phases={IMPORT_PROCESSING_PHASES}
              intervalMs={2200}
              variant="text"
              testid="import-processing-phase"
            />
          ) : staticSubline}
        </p>
      </div>
    </div>
  );
}


/* Shown once per deck immediately after the founder finishes the Brand step
   and lands on Cover & Snapshot — offers to bootstrap the deck from an
   existing .pptx or company doc so they don't have to type everything from
   scratch. Dismissed choice is persisted in localStorage per deck_id. */
function ImportPromptModal({ onUpload, onDecline }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4"
      style={{ backgroundColor: "rgba(0,0,0,0.8)" }}
      onClick={onDecline}
      data-testid="import-prompt-modal">
      <div className="rounded-2xl max-w-md w-full p-7 relative border"
        style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.22)" }}
        onClick={(e) => e.stopPropagation()}>
        <button onClick={onDecline}
          data-testid="import-prompt-close"
          className="absolute top-4 right-4 text-white/40 hover:text-white/80 p-1">
          <X size={18} />
        </button>
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold"
          style={{ color: C.tealSoft }}>
          <UploadSimple size={12} weight="fill" /> Quick head-start
        </div>
        <h3 className="font-extrabold text-2xl mt-3 tracking-tight" style={{ letterSpacing: "-0.02em" }}>
          Skip the blank page.
        </h3>
        <p className="text-white/70 mt-2 text-[14px] leading-relaxed">
          Got an old pitch deck, one-pager, or company doc? Upload it and our AI will
          map the content into the 11-slide methodology so you can polish rather than
          write from scratch.
        </p>
        <p className="text-white/45 mt-2 text-[12px]">
          Supports .pptx and .pdf up to 40 MB · costs <strong className="text-white/80">5 credits</strong>.
        </p>

        <div className="mt-6 flex flex-col gap-2">
          <button onClick={onUpload}
            data-testid="import-prompt-upload"
            className="w-full text-sm font-bold px-4 py-3 rounded-md inline-flex items-center justify-center gap-2 transition-colors"
            style={{ backgroundColor: C.teal, color: "#fff" }}>
            <UploadSimple size={15} weight="bold" />
            Yes, upload my existing deck · 5 cr
          </button>
          <button onClick={onDecline}
            data-testid="import-prompt-decline"
            className="w-full text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-white/5 text-white/60 transition-colors">
            No thanks, I&apos;ll fill it in myself
          </button>
        </div>
      </div>
    </div>
  );
}


function ShareModal({ deck, deckId, onClose, onUpdate }) {
  const [isPublic, setIsPublic] = useState(!!deck.is_public);
  const [busy, setBusy] = useState(false);
  const shareUrl = `${window.location.origin}/p/${deckId}`;

  const toggle = async (next) => {
    setBusy(true);
    try {
      await api.put(`/pitch/decks/${deckId}/share`, { is_public: next });
      setIsPublic(next);
      onUpdate(next);
      toast.success(next ? "Public link enabled" : "Public link disabled");
    } catch { toast.error("Could not update sharing"); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch { toast.error("Copy failed — select & copy manually"); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4" style={{ backgroundColor: "rgba(0,0,0,0.75)" }}
      data-testid="share-modal" onClick={onClose}>
      <div className="rounded-2xl max-w-lg w-full p-8 relative border" onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.2)" }}>
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold mb-2" style={{ color: C.orange }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: C.orange }}></span> One-click share
        </div>
        <h3 className="font-extrabold text-3xl tracking-tight" style={{ letterSpacing: "-0.02em" }}>
          Send <span style={{ color: C.teal }}>{deck.company_name}</span> to investors.
        </h3>
        <p className="text-white/65 mt-2 mb-6 text-sm leading-relaxed">
          Toggle a read-only public link. Anyone with the URL can view your deck — no Clinq account needed.
          Disable any time and the link stops working immediately.
        </p>

        <div className="rounded-xl border p-4 mb-5 flex items-center justify-between"
          style={{ borderColor: "rgba(123, 211, 206, 0.18)", backgroundColor: "rgba(0,0,0,0.3)" }}>
          <div className="flex items-center gap-3">
            {isPublic ? <Eye size={20} style={{ color: C.teal }} /> : <EyeSlash size={20} className="text-white/40" />}
            <div>
              <div className="text-sm font-bold">{isPublic ? "Public link is ON" : "Public link is OFF"}</div>
              <div className="text-xs text-white/55">
                {isPublic ? "Anyone with the link can view this deck." : "Only you can view this deck right now."}
              </div>
            </div>
          </div>
          <button onClick={() => toggle(!isPublic)} disabled={busy} data-testid="toggle-share"
            className="text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-md transition-colors disabled:opacity-60"
            style={{ backgroundColor: isPublic ? "rgba(255,255,255,0.1)" : C.teal, color: "#fff", border: isPublic ? "1px solid rgba(255,255,255,0.15)" : "none" }}>
            {busy ? "…" : (isPublic ? "Turn off" : "Turn on")}
          </button>
        </div>

        {isPublic && (
          <div data-testid="share-url-block">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-2" style={{ color: C.tealSoft }}>Public link</div>
            <div className="flex items-center gap-2">
              <input readOnly value={shareUrl} data-testid="share-url-input"
                className="flex-1 px-3 py-2 rounded-md text-xs font-mono"
                style={{ backgroundColor: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }} />
              <button onClick={copy} data-testid="copy-share-url"
                className="px-3 py-2 rounded-md font-semibold text-xs inline-flex items-center gap-1.5"
                style={{ backgroundColor: C.teal, color: "#fff" }}>
                <Copy size={12} /> Copy
              </button>
              <a href={shareUrl} target="_blank" rel="noreferrer" data-testid="open-share-url"
                className="px-3 py-2 rounded-md font-semibold text-xs inline-flex items-center gap-1.5 hover:bg-white/10"
                style={{ border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }}>
                Open ↗
              </a>
            </div>
            <p className="text-[11px] text-white/40 mt-3 leading-relaxed">
              Tip: viewers see your latest saved content. Edits you make here go live the moment you save.
            </p>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} data-testid="close-share-modal"
            className="text-xs font-semibold px-4 py-2 rounded-md hover:bg-white/10"
            style={{ border: "1px solid rgba(255,255,255,0.15)" }}>Done</button>
        </div>
      </div>
    </div>
  );
}

function BrandView({ deck, onBack, onUploadLogo, onRemoveLogo, onColorChange, logoInputRef, onLogoFile }) {
  const [color, setColor] = useState(deck.brand_color || "#0A8B85");
  const [font, setFont] = useState(deck.font_choice || "inter");
  const [tagline, setTagline] = useState(deck.tagline || "");
  const isV2 = (deck.design_version || "v1") === "v2";

  // Keep local state in sync if deck reloads from server
  useEffect(() => {
    if (deck.brand_color && deck.brand_color !== color) setColor(deck.brand_color);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deck.brand_color]);
  useEffect(() => {
    if (deck.font_choice && deck.font_choice !== font) setFont(deck.font_choice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deck.font_choice]);
  useEffect(() => {
    if ((deck.tagline || "") !== tagline) setTagline(deck.tagline || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deck.tagline]);

  // Debounce-save the colour to the backend on every change so it persists
  // across reload (covers both color-picker and text input).
  useEffect(() => {
    if (!color) return;
    if (color === deck.brand_color) return;
    // Only persist valid 7-char hex values to avoid 400s while user is typing.
    if (!/^#[0-9a-fA-F]{6}$/.test(color)) return;
    const t = setTimeout(() => { onColorChange(color); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color]);

  // Persist font + tagline (V2 only)
  const saveBrandField = async (field, value) => {
    try {
      await api.put(`/pitch/decks/${deck.deck_id}/brand`, { [field]: value });
    } catch (e) {
      toastApiError(e, `Could not save ${field}`, {
        context: { deck_id: deck.deck_id, action: "save_field", field },
      });
    }
  };
  useEffect(() => {
    if (!isV2) return;
    if (font === (deck.font_choice || "inter")) return;
    const t = setTimeout(() => saveBrandField("font_choice", font), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [font]);
  useEffect(() => {
    if (!isV2) return;
    if (tagline === (deck.tagline || "")) return;
    const t = setTimeout(() => saveBrandField("tagline", tagline), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagline]);

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="brand-view">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white mb-6">
        <ArrowLeft size={14} /> Back to slides
      </button>
      <div className="text-[11px] uppercase tracking-[0.25em] font-semibold mb-3" style={{ color: C.orange }}>Brand</div>
      <h1 className="font-extrabold text-4xl tracking-tight" style={{ letterSpacing: "-0.03em" }}>
        Make it look like <span style={{ color: C.teal }}>your business</span>.
      </h1>
      <p className="text-white/60 mt-3 max-w-2xl">
        Upload your logo and pick a brand colour — they appear on every slide of the generated deck.
      </p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Logo */}
        <div className="p-6 rounded-2xl border" style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.15)" }}>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] mb-4" style={{ color: C.tealSoft }}>
            <FileImage size={14} className="inline mr-2" weight="bold" /> Logo
          </div>
          {deck.logo ? (
            <div>
              <div className="rounded-md bg-white/95 p-6 grid place-items-center mb-3" style={{ minHeight: "120px" }}>
                <img src={deck.logo} alt="Logo" className="max-h-24 max-w-full object-contain" data-testid="brand-logo-preview" />
              </div>
              <div className="flex items-center gap-2">
                <button onClick={onUploadLogo} className="text-xs font-semibold px-3 py-1.5 rounded-md transition-colors hover:bg-white/10"
                  style={{ border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }} data-testid="replace-logo-btn">Replace</button>
                <button onClick={onRemoveLogo} className="text-xs font-semibold px-3 py-1.5 rounded-md text-orange-400 hover:bg-orange-500/10 transition-colors" data-testid="remove-logo-btn">Remove</button>
              </div>
            </div>
          ) : (
            <button onClick={onUploadLogo} data-testid="upload-logo-btn"
              className="w-full rounded-lg py-10 px-4 text-center border border-dashed hover:bg-white/5 transition-colors"
              style={{ borderColor: "rgba(255,255,255,0.2)" }}>
              <UploadSimple size={28} className="mx-auto mb-3" style={{ color: C.tealSoft }} />
              <div className="text-sm font-semibold">Upload your logo</div>
              <div className="text-[11px] text-white/40 mt-1">PNG, JPG, WEBP or SVG · up to 2 MB</div>
            </button>
          )}
          <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" style={{ display: "none" }}
            data-testid="logo-file-input" onChange={(e) => onLogoFile(e.target.files?.[0])} />
        </div>

        {/* Brand colour */}
        <div className="p-6 rounded-2xl border" style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.15)" }}>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] mb-4" style={{ color: C.tealSoft }}>
            <Palette size={14} className="inline mr-2" weight="bold" /> Brand colour
          </div>
          <div className="rounded-md mb-3 grid place-items-center" style={{ minHeight: "120px", backgroundColor: color }}>
            <span className="font-mono-num text-sm font-bold" style={{ color: "#fff", mixBlendMode: "difference" }}>{color.toUpperCase()}</span>
          </div>
          <div className="flex items-center gap-2">
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
              className="w-12 h-9 rounded-md cursor-pointer" data-testid="brand-color-picker"
              style={{ border: "1px solid rgba(255,255,255,0.15)", background: "transparent" }} />
            <input type="text" value={color} onChange={(e) => setColor(e.target.value)}
              className="flex-1 px-3 py-2 rounded-md text-sm font-mono"
              style={{ backgroundColor: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }}
              data-testid="brand-color-input" />
          </div>
          <div className="text-[11px] text-white/40 mt-3 leading-relaxed">
            <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: C.tealSoft }}>
              <Sparkle size={11} weight="fill" /> Auto-picked
            </span>{" "}
            when you upload a logo we sample the dominant colour and set it here — override any time.
          </div>
        </div>

        {isV2 && (
          <>
            {/* Tagline */}
            <div className="p-6 rounded-2xl border" style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.15)" }}>
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] mb-4" style={{ color: C.tealSoft }}>
                <Sparkle size={14} className="inline mr-2" weight="bold" /> Tagline
              </div>
              <input type="text" value={tagline} onChange={(e) => setTagline(e.target.value.slice(0, 120))}
                placeholder="e.g. The fastest way for African businesses to get funded."
                data-testid="brand-tagline-input"
                className="w-full px-3 py-2.5 rounded-md text-sm"
                style={{ backgroundColor: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }} />
              {/* Iter91: colour-coded tagline counter — mirrors the
                  iter89/90 nudge pattern. Sweet spot ≤60 chars (fits
                  on one line in the cover hero), 60-100 wraps to
                  two lines (still fine), 100-120 tight but valid. */}
              {(() => {
                const n = tagline.length;
                const tone =
                  n === 0 ? "text-white/30"
                    : n <= 60 ? "text-emerald-400/80"
                      : n <= 100 ? "text-white/50"
                        : "text-amber-400";
                const hint =
                  n === 0 ? "One-line pitch (≤60 chars for best fit)"
                    : n <= 60 ? "Fits on one line in the hero"
                      : n <= 100 ? "Will wrap to two lines"
                        : "Tight — funders scan one-liners";
                return (
                  <>
                    <div
                      data-testid="brand-tagline-counter"
                      className={`text-[11px] font-mono-num tabular-nums mt-2 flex items-baseline gap-2 ${tone}`}
                      title={hint}
                    >
                      <span>{n}/120</span>
                      <span className="text-white/25">·</span>
                      <span>{hint}</span>
                    </div>
                    <div className="text-[11px] text-white/40 mt-1 leading-relaxed">
                      Appears beneath your company name on the cover slide. Keep it under 12 words.
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Font pair */}
            <div className="p-6 rounded-2xl border" style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.15)" }}>
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] mb-4" style={{ color: C.tealSoft }}>
                <BookOpen size={14} className="inline mr-2" weight="bold" /> Typeface
              </div>
              <div className="grid grid-cols-1 gap-2" data-testid="brand-font-options">
                {[
                  {
                    id: "inter",
                    label: "Inter Bold + Inter Regular",
                    preview: "Modern · neutral · investor-default",
                    labelFont: "'Inter', sans-serif",
                    previewFont: "'Inter', sans-serif",
                  },
                  {
                    id: "montserrat",
                    label: "Montserrat Bold + Lato Regular",
                    preview: "Editorial · confident · warm",
                    labelFont: "'Montserrat', sans-serif",
                    previewFont: "'Lato', sans-serif",
                  },
                  {
                    id: "raleway",
                    label: "Raleway Bold + Open Sans Regular",
                    preview: "Polished · friendly · approachable",
                    labelFont: "'Raleway', sans-serif",
                    previewFont: "'Open Sans', sans-serif",
                  },
                ].map((opt) => {
                  const active = font === opt.id;
                  return (
                    <button key={opt.id} onClick={() => setFont(opt.id)}
                      data-testid={`brand-font-${opt.id}`}
                      className="text-left p-3 rounded-md transition-colors"
                      style={{
                        backgroundColor: active ? "rgba(10, 139, 133, 0.15)" : "rgba(0,0,0,0.3)",
                        border: `1px solid ${active ? C.teal : "rgba(255,255,255,0.1)"}`,
                      }}>
                      <div className="text-sm text-white"
                        style={{ fontFamily: opt.labelFont, fontWeight: 700 }}>
                        {opt.label}
                      </div>
                      <div className="text-[11px] text-white/55 mt-0.5"
                        style={{ fontFamily: opt.previewFont, fontWeight: 400 }}>
                        {opt.preview}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

          </>
        )}
      </div>
    </div>
  );
}

function CreatedDeckModal({ blob, deck, onDownload, onClose }) {
  const sizeKb = blob ? Math.round(blob.size / 1024) : 0;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4" style={{ backgroundColor: "rgba(0,0,0,0.8)" }}
      data-testid="created-deck-modal" onClick={onClose}>
      <div className="rounded-2xl max-w-lg w-full p-8 relative border" onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.2)" }}>
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold" style={{ color: C.tealSoft }}>
          <CheckCircle size={12} weight="fill" style={{ color: C.teal }} /> Ready
        </div>
        <h3 className="font-extrabold text-3xl mt-3 tracking-tight" style={{ letterSpacing: "-0.02em" }}>
          Your pitch deck is <span style={{ color: C.teal }}>ready</span>.
        </h3>
        <p className="text-white/65 mt-2 mb-6 text-sm leading-relaxed">
          Generated with your logo, brand colour, content and uploaded photos. {sizeKb} KB · {deck.slides.length} core slides + extras.
        </p>
        <div className="flex items-center gap-3">
          <button onClick={onDownload} data-testid="download-deck-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-md font-bold text-sm transition-colors"
            style={{ backgroundColor: C.teal, color: "#fff" }}>
            <DownloadSimple size={14} weight="bold" /> Download .pptx
          </button>
          <button onClick={onClose} data-testid="close-created-modal"
            className="px-5 py-3 rounded-md font-semibold text-sm hover:bg-white/10 transition-colors"
            style={{ border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// Inline contact panel used on Slide 12 (replaces Data Room Index)
function InlineClosing({ deckId, closing, onSaved }) {
  const safe = closing || {};
  const [form, setForm] = useState({
    headline: safe.headline || "Thank you.",
    tagline:  safe.tagline  || "Let's keep the conversation going.",
    website:  safe.website  || "",
    email:    safe.email    || "",
    phone:    safe.phone    || "",
    address:  safe.address  || "",
    extra:    safe.extra    || "",
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/pitch/decks/${deckId}/closing`, form);
      onSaved && onSaved(data.closing);
      setSavedAt(Date.now());
      toast.success("Closing slide saved.");
    } catch (e) {
      toastApiError(e, "Could not save closing slide.", {
        context: { deck_id: deckId, action: "save_closing_slide" },
      });
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-6 space-y-5" data-testid="inline-closing">
      <div className="rounded-xl border p-4 flex items-start gap-3"
        style={{ backgroundColor: "rgba(123, 211, 206, 0.06)", borderColor: "rgba(123, 211, 206, 0.18)" }}>
        <Sparkle size={14} weight="fill" style={{ color: C.tealSoft }} className="mt-1 shrink-0" />
        <div className="text-[13px] leading-relaxed text-white/75">
          The closing slide. Fill in how funders should reach you — these appear on slide 12 of the exported deck.
        </div>
      </div>

      <ClosingField label="Headline" testid="closing-headline" hint="Big text at the top (default: 'Thank you.')">
        <input type="text" value={form.headline} maxLength={60} onChange={(e) => update("headline", e.target.value)}
          data-testid="closing-headline-input"
          className="w-full px-3 py-2.5 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0A8B85]"
          style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
      </ClosingField>

      <ClosingField label="Tagline" testid="closing-tagline" hint="One short line below the headline">
        <input type="text" value={form.tagline} maxLength={120} onChange={(e) => update("tagline", e.target.value)}
          data-testid="closing-tagline-input" placeholder="Let's keep the conversation going."
          className="w-full px-3 py-2.5 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0A8B85]"
          style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
      </ClosingField>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ClosingField label="Website" testid="closing-website">
          <input type="url" value={form.website} onChange={(e) => update("website", e.target.value)}
            data-testid="closing-website-input" placeholder="https://yourcompany.africa"
            className="w-full px-3 py-2.5 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0A8B85]"
            style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
        </ClosingField>
        <ClosingField label="Email" testid="closing-email">
          <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
            data-testid="closing-email-input" placeholder="founders@yourcompany.com"
            className="w-full px-3 py-2.5 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0A8B85]"
            style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
        </ClosingField>
        <ClosingField label="Phone" testid="closing-phone">
          <input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)}
            data-testid="closing-phone-input" placeholder="+254 700 000 000"
            className="w-full px-3 py-2.5 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0A8B85]"
            style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
        </ClosingField>
        <ClosingField label="Address" testid="closing-address">
          <input type="text" value={form.address} onChange={(e) => update("address", e.target.value)}
            data-testid="closing-address-input" placeholder="Nairobi, Kenya"
            className="w-full px-3 py-2.5 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0A8B85]"
            style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
        </ClosingField>
      </div>

      <ClosingField label="Anything else" testid="closing-extra" hint="LinkedIn, Twitter/X, calendar link — one line.">
        <input type="text" value={form.extra} onChange={(e) => update("extra", e.target.value)}
          data-testid="closing-extra-input" placeholder="linkedin.com/in/yourname · calendly.com/yourname"
          className="w-full px-3 py-2.5 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0A8B85]"
          style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
      </ClosingField>

      <div className="flex items-center gap-3 pt-2">
        <button onClick={save} disabled={saving} data-testid="closing-save"
          className="px-4 py-2 rounded-md text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60"
          style={{ backgroundColor: C.teal, color: "#fff" }}>
          <FloppyDisk size={14} weight="fill" /> {saving ? "Saving…" : "Save closing slide"}
        </button>
        {savedAt && <span className="text-[11px] text-white/40">Saved.</span>}
      </div>
    </div>
  );
}

function ClosingField({ label, hint, testid, children }) {
  return (
    <div data-testid={`${testid}-field`}>
      <label className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-1.5 block">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-white/35 mt-1.5">{hint}</div>}
    </div>
  );
}



// ============================================================================
// Slide 9 — Team roster
// ============================================================================
function InlineTeam({ deckId, team, onSaved }) {
  const [members, setMembers] = useState(() =>
    (team && team.length ? team : []).map((m) => ({ ...m })));
  const [saving, setSaving] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);
  const fileRefs = useRef({});

  const addMember = () => {
    setMembers((arr) => [...arr, {
      id: `tmp_${Date.now()}_${arr.length}`,
      name: "", role: "", experience: "",
      photo_url: null, photo_storage_path: null, photo_id: null,
    }]);
  };
  const removeMember = (id) => setMembers((arr) => arr.filter((m) => m.id !== id));
  const update = (id, field, value) =>
    setMembers((arr) => arr.map((m) => (m.id === id ? { ...m, [field]: value } : m)));

  const uploadPhoto = async (member, file) => {
    if (!file) return;
    setUploadingFor(member.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/pitch/decks/${deckId}/team/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update(member.id, "photo_url", data.photo_url);
      update(member.id, "photo_storage_path", data.photo_storage_path);
      update(member.id, "photo_id", data.photo_id);
      update(member.id, "content_type", data.content_type);
    } catch (e) {
      toastApiError(e, "Photo upload failed.", {
        context: { deck_id: deckId, action: "team_photo_upload" },
      });
    } finally { setUploadingFor(null); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = members
        .filter((m) => (m.name || "").trim())
        .map(({ id, name, role, experience, photo_url, photo_storage_path, photo_id, content_type }) => ({
          id: id && !String(id).startsWith("tmp_") ? id : undefined,
          name, role, experience, photo_url, photo_storage_path, photo_id, content_type,
        }));
      const { data } = await api.put(`/pitch/decks/${deckId}/team`, { members: payload });
      onSaved && onSaved(data.team);
      setMembers(data.team.map((m) => ({ ...m })));
      toast.success(`Saved ${data.team.length} team member${data.team.length === 1 ? "" : "s"}.`);
    } catch (e) {
      toastApiError(e, "Could not save team.", { context: { deck_id: deckId, action: "save_team" } });
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-6 space-y-4" data-testid="inline-team">
      <div className="rounded-xl border p-4 flex items-start gap-3"
        style={{ backgroundColor: "rgba(123, 211, 206, 0.06)", borderColor: "rgba(123, 211, 206, 0.18)" }}>
        <Sparkle size={14} weight="fill" style={{ color: C.tealSoft }} className="mt-1 shrink-0" />
        <div className="text-[13px] leading-relaxed text-white/75">
          Add up to 6 founders &amp; key hires. Funders read this slide first to decide if you can execute — name credible prior employers (e.g. &ldquo;ex-Safaricom&rdquo;, &ldquo;5y at Andela&rdquo;).
        </div>
      </div>

      {members.length === 0 && (
        <div className="rounded-lg border border-dashed border-white/15 px-4 py-8 text-center text-white/45 text-sm" data-testid="team-empty">
          No team members yet. Add your founders below.
        </div>
      )}

      <div className="space-y-3">
        {members.map((m, idx) => (
          <div key={m.id} className="rounded-lg border p-4 grid grid-cols-12 gap-3"
            style={{ borderColor: "rgba(255,255,255,0.1)", backgroundColor: "rgba(0,0,0,0.25)" }}
            data-testid={`team-member-${idx}`}>
            <div className="col-span-12 sm:col-span-3 flex flex-col items-center gap-2">
              <div className="w-24 h-24 rounded-full overflow-hidden grid place-items-center"
                style={{ backgroundColor: "rgba(10, 139, 133, 0.18)", border: `2px solid ${C.teal}55` }}>
                {m.photo_url ? (
                  <img src={m.photo_url.startsWith("http") || m.photo_url.startsWith("data:") ? m.photo_url : `${process.env.REACT_APP_BACKEND_URL}${m.photo_url}`}
                    alt={m.name || "team"} className="w-full h-full object-cover" data-testid={`team-photo-${idx}`} />
                ) : (
                  <span className="text-2xl font-extrabold" style={{ color: C.tealSoft }}>
                    {(m.name || "?").split(" ").slice(0, 2).map((p) => p[0] || "").join("").toUpperCase() || "?"}
                  </span>
                )}
              </div>
              <input type="file" accept="image/png,image/jpeg,image/webp"
                ref={(el) => { fileRefs.current[m.id] = el; }}
                style={{ display: "none" }}
                data-testid={`team-photo-input-${idx}`}
                onChange={(e) => uploadPhoto(m, e.target.files?.[0])} />
              <button onClick={() => fileRefs.current[m.id]?.click()}
                disabled={uploadingFor === m.id}
                data-testid={`team-upload-${idx}`}
                className="text-[11px] font-semibold px-2.5 py-1 rounded inline-flex items-center gap-1.5 disabled:opacity-50"
                style={{ border: "1px solid rgba(123,211,206,0.3)", color: C.tealSoft }}>
                <UploadSimple size={11} /> {uploadingFor === m.id ? "Uploading…" : (m.photo_url ? "Change" : "Add photo")}
              </button>
              {/* Iter103: rotating status while the photo uploads +
                  server-side processes (resize, thumbnail, storage).
                  Keeps the founder oriented during the 8-20s wait. */}
              {uploadingFor === m.id && (
                <PhaseIndicator
                  phases={TEAM_PHOTO_UPLOAD_PHASES}
                  intervalMs={1800}
                  testid={`team-upload-phase-${idx}`}
                  className="mt-1"
                />
              )}
            </div>

            <div className="col-span-12 sm:col-span-9 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/45 font-bold mb-1.5 block">Name</label>
                  <input type="text" value={m.name || ""} maxLength={80}
                    onChange={(e) => update(m.id, "name", e.target.value)}
                    placeholder="Jane Doe" data-testid={`team-name-${idx}`}
                    className="w-full px-3 py-2 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0A8B85]"
                    style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/45 font-bold mb-1.5 block">Role</label>
                  <input type="text" value={m.role || ""} maxLength={120}
                    onChange={(e) => update(m.id, "role", e.target.value)}
                    placeholder="Co-founder &amp; CEO" data-testid={`team-role-${idx}`}
                    className="w-full px-3 py-2 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0A8B85]"
                    style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
                </div>
              </div>
              <div>
                <div className="flex items-baseline justify-between mb-1.5">
                  <label className="text-[10px] uppercase tracking-widest text-white/45 font-bold block">
                    Past experience
                  </label>
                  {/* Iter89: inline word counter — nudges founders toward
                      funder-friendly punchy bios (40–80 words is the
                      sweet spot for the 1-row card layout). Colour
                      transitions from muted → amber → red at 80/120. */}
                  {(() => {
                    const words = (m.experience || "").trim().split(/\s+/).filter(Boolean).length;
                    const tone =
                      words === 0 ? "text-white/30"
                        : words <= 80 ? "text-white/50"
                          : words <= 120 ? "text-amber-400"
                            : "text-red-400";
                    const hint =
                      words === 0 ? "40–80 words reads best on the slide card"
                        : words <= 80 ? ""
                          : words <= 120 ? "Getting long — consider trimming"
                            : "Likely to overflow the card";
                    return (
                      <span
                        data-testid={`team-exp-wordcount-${idx}`}
                        className={`text-[10px] font-mono tabular-nums ${tone}`}
                        title={hint}
                      >
                        {words} {words === 1 ? "word" : "words"}
                        {hint && <span className="hidden sm:inline"> · {hint}</span>}
                      </span>
                    );
                  })()}
                </div>
                <textarea value={m.experience || ""} maxLength={1200} rows={4}
                  onChange={(e) => update(m.id, "experience", e.target.value)}
                  placeholder="ex-Safaricom (5y, Product) · led mobile-money rollout to 2M users · MBA Strathmore"
                  data-testid={`team-exp-${idx}`}
                  className="w-full px-3 py-2 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0A8B85] resize-y"
                  style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
              </div>
            </div>

            <div className="col-span-12 flex justify-end">
              <button onClick={() => removeMember(m.id)} data-testid={`team-remove-${idx}`}
                className="text-[11px] font-semibold inline-flex items-center gap-1 text-red-400 hover:text-red-300">
                <Trash size={11} /> Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        <button onClick={addMember} disabled={members.length >= 6} data-testid="team-add"
          className="text-sm font-semibold px-3 py-2 rounded-md inline-flex items-center gap-1.5 disabled:opacity-40"
          style={{ border: "1px solid rgba(255,255,255,0.2)", color: "#fff" }}>
          <Plus size={13} weight="bold" /> Add team member
        </button>
        <button onClick={save} disabled={saving} data-testid="team-save"
          className="px-4 py-2 rounded-md text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60"
          style={{ backgroundColor: C.teal, color: "#fff" }}>
          <FloppyDisk size={14} weight="fill" /> {saving ? "Saving…" : "Save team"}
        </button>
      </div>
    </div>
  );
}


// ============================================================================
// Slide 10 — Financial Projections table
// ============================================================================
function InlineProjections({ deckId, projections, onSaved }) {
  const currentYear = new Date().getFullYear();
  const initialProj = projections || { currency: "KES", base_year: currentYear, years: [] };
  const [currency, setCurrency] = useState(initialProj.currency || "KES");
  const [rows, setRows] = useState(() => initialProj.years && initialProj.years.length
    ? initialProj.years.map((y) => ({ ...y }))
    : []);
  const [cac, setCac] = useState(() => ({
    value: (initialProj.cac && Number.isFinite(initialProj.cac.value)) ? initialProj.cac.value : 0,
    kind: (initialProj.cac && initialProj.cac.kind) || "current",
  }));
  const [ltv, setLtv] = useState(() => ({
    value: (initialProj.ltv && Number.isFinite(initialProj.ltv.value)) ? initialProj.ltv.value : 0,
    kind: (initialProj.ltv && initialProj.ltv.kind) || "current",
  }));
  const [saving, setSaving] = useState(false);

  const addRow = () => {
    if (rows.length >= 6) return;
    const lastYear = rows.length ? Math.max(...rows.map((r) => r.year)) : currentYear - 1;
    setRows((arr) => [...arr, { year: lastYear + 1, revenue: 0, cost_of_sales: 0, opex: 0 }]);
  };
  const removeRow = (idx) => setRows((arr) => arr.filter((_, i) => i !== idx));
  const updateRow = (idx, field, value) => {
    const v = field === "year" ? parseInt(value || "0", 10) : parseFloat(value || "0");
    setRows((arr) => arr.map((r, i) => (i === idx ? { ...r, [field]: isNaN(v) ? 0 : v } : r)));
  };

  const computed = rows.map((r, i) => {
    const rev = r.revenue || 0, cos = r.cost_of_sales || 0, opex = r.opex || 0;
    const gp = rev - cos;
    const gm = rev ? (gp / rev) * 100 : 0;
    const op = gp - opex;
    // Iter98: mirror the preview/PPTX — expose Operational Profit Margin
    // as a derived field so the editor's projections table stays in sync
    // with what funders will see on the slide.
    const opm = rev ? (op / rev) * 100 : null;
    const prev = i > 0 ? (rows[i - 1].revenue || 0) : null;
    const growth = (prev && prev !== 0) ? ((rev - prev) / prev) * 100 : null;
    return { ...r, gross_profit: gp, gross_margin: gm, op_profit: op, op_margin: opm, growth };
  });

  // LTV / CAC — auto-computed ratio shown in "1.5x" format. Guard against
  // divide-by-zero (returns null → renders as a muted em-dash).
  const ltvCacRatio = (cac.value && cac.value > 0)
    ? (ltv.value || 0) / cac.value
    : null;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/pitch/decks/${deckId}/projections`, {
        currency, base_year: currentYear,
        years: rows.map(({ year, revenue, cost_of_sales, opex }) => ({ year, revenue, cost_of_sales, opex })),
        cac: { value: Number(cac.value) || 0, kind: cac.kind },
        ltv: { value: Number(ltv.value) || 0, kind: ltv.kind },
      });
      onSaved && onSaved(data.projections);
      toast.success("Financial projections saved.");
    } catch (e) {
      toastApiError(e, "Could not save projections.", {
        context: { deck_id: deckId, action: "save_projections" },
      });
    } finally { setSaving(false); }
  };

  const importFromModel = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/pitch/decks/${deckId}/projections/import-fm`);
      const pj = data.projections || {};
      setRows((pj.years || []).map((y) => ({ ...y })));
      if (pj.currency) setCurrency(pj.currency);
      if (pj.cac) setCac({ value: pj.cac.value, kind: pj.cac.kind || "forecast" });
      if (pj.ltv) setLtv({ value: pj.ltv.value, kind: pj.ltv.kind || "forecast" });
      onSaved && onSaved(pj);
      toast.success(`Imported F1–F5 from "${data.model_name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not import from your financial model");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-6 space-y-4" data-testid="inline-projections">
      <div className="rounded-xl border p-4 flex items-start gap-3"
        style={{ backgroundColor: "rgba(245, 158, 11, 0.05)", borderColor: "rgba(245, 158, 11, 0.2)" }}>
        <Sparkle size={14} weight="fill" style={{ color: C.orange }} className="mt-1 shrink-0" />
        <div className="text-[13px] leading-relaxed text-white/75">
          Add current-year actuals (post-revenue) plus up to 5 forecast years. Gross margin, operational profit and revenue growth are computed automatically.
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-[10px] uppercase tracking-widest text-white/45 font-bold">Currency</label>
        <input type="text" value={currency} maxLength={8}
          onChange={(e) => setCurrency(e.target.value)}
          data-testid="projections-currency"
          className="px-3 py-1.5 rounded-md text-xs text-white font-mono-num w-24 focus:outline-none focus:border-[#0A8B85]"
          style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
        <span className="text-[11px] text-white/35">e.g. KES, USD, EUR</span>
        <button onClick={importFromModel} disabled={saving} data-testid="projections-import-fm"
          className="ml-auto text-xs font-bold px-3 py-1.5 rounded-md disabled:opacity-50"
          style={{ backgroundColor: "rgba(10,139,133,0.2)", border: "1px solid rgba(10,139,133,0.5)", color: "#7BD3CE" }}>
          {saving ? "Importing…" : "Import from financial model"}
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-white/15 px-4 py-8 text-center text-white/45 text-sm" data-testid="projections-empty">
          No years added yet. Click <strong className="text-white/70">Add year</strong> below to start with the current year.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
          <table className="w-full text-sm" data-testid="projections-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-white/45 font-bold" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
                <th className="px-3 py-2.5 text-left">Metric</th>
                {computed.map((r, i) => (
                  <th key={i} className="px-3 py-2.5 text-center font-mono-num" style={{ color: C.tealSoft }}>
                    <input type="number" value={r.year || ""} onChange={(e) => updateRow(i, "year", e.target.value)}
                      data-testid={`proj-year-${i}`}
                      className="w-20 text-center bg-transparent text-[13px] font-bold focus:outline-none"
                      style={{ color: C.tealSoft }} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <ProjRow label="Revenue" rows={computed} field="revenue" onChange={updateRow} editable currency={currency} testid="proj-revenue" />
              <ProjRow label="Cost of sales" rows={computed} field="cost_of_sales" onChange={updateRow} editable currency={currency} testid="proj-cos" />
              <ProjRow label="Gross profit" rows={computed} field="gross_profit" testid="proj-gp" derived />
              <ProjRow label="Gross margin %" rows={computed} field="gross_margin" testid="proj-gm" derived isPct />
              <ProjRow label="Operational expenses" rows={computed} field="opex" onChange={updateRow} editable currency={currency} testid="proj-opex" />
              <ProjRow label="Operational profit" rows={computed} field="op_profit" testid="proj-op" derived bold />
              {/* Iter98: match the preview/PPTX row order — Op profit margin
                  is derived immediately after Operational profit so the
                  editor tells the same story as the rendered slide. */}
              <ProjRow label="Op profit margin %" rows={computed} field="op_margin" testid="proj-opm" derived isPct nullable />
              <ProjRow label="Revenue growth YoY %" rows={computed} field="growth" testid="proj-growth" derived isPct nullable />
              <tr className="border-t border-white/10">
                <td className="px-3 py-2 text-[10px] uppercase tracking-wider text-white/35 font-bold">Actions</td>
                {computed.map((_, i) => (
                  <td key={i} className="px-2 py-2 text-center">
                    <button onClick={() => removeRow(i)} data-testid={`proj-remove-${i}`}
                      className="text-[10px] text-red-400 hover:text-red-300 inline-flex items-center gap-1">
                      <Trash size={10} /> Remove
                    </button>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="pt-2">
        <button onClick={addRow} disabled={rows.length >= 6} data-testid="proj-add-year"
          className="text-sm font-semibold px-3 py-2 rounded-md inline-flex items-center gap-1.5 disabled:opacity-40"
          style={{ border: "1px solid rgba(255,255,255,0.2)", color: "#fff" }}>
          <Plus size={13} weight="bold" /> Add year {rows.length >= 6 && "(max 6)"}
        </button>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          Unit Economics — CAC / LTV / LTV-CAC (iter70)
          Founders enter a CAC value + LTV value in the same currency
          as the projections above; each is tagged Current or Forecast
          so funders can tell what is proof versus plan. The LTV/CAC
          ratio is auto-computed and displayed in "1.5x" format.
      ───────────────────────────────────────────────────────────────── */}
      <div data-testid="unit-econ-block"
        className="mt-6 rounded-xl p-5 space-y-4"
        style={{ backgroundColor: "rgba(10,139,133,0.06)",
                 border: "1px solid rgba(10,139,133,0.22)" }}>
        <div className="flex items-start gap-2">
          <div className="text-[11px] font-extrabold uppercase tracking-[0.22em]" style={{ color: C.teal }}>
            Unit Economics
          </div>
          <span className="text-[10.5px] text-white/45 italic">optional but funders love to see it</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* CAC */}
          <UnitEconInput
            testidPrefix="unit-econ-cac"
            label="CAC"
            title="Customer Acquisition Cost"
            info="CAC = Customer Acquisition Cost. The average amount you spend on sales, marketing and onboarding to land one paying customer. Lower = more capital-efficient growth."
            currency={currency}
            value={cac.value}
            kind={cac.kind}
            onValue={(v) => setCac((s) => ({ ...s, value: v }))}
            onKind={(k) => setCac((s) => ({ ...s, kind: k }))}
          />
          {/* LTV */}
          <UnitEconInput
            testidPrefix="unit-econ-ltv"
            label="LTV"
            title="Lifetime Value"
            info="LTV = Lifetime Value. The total gross-profit contribution a single customer makes across the whole time they stay a customer. Higher = customers repay their acquisition cost many times over."
            currency={currency}
            value={ltv.value}
            kind={ltv.kind}
            onValue={(v) => setLtv((s) => ({ ...s, value: v }))}
            onKind={(k) => setLtv((s) => ({ ...s, kind: k }))}
          />
          {/* LTV/CAC ratio — read-only */}
          <div className="rounded-lg p-4 flex flex-col justify-between"
            style={{ backgroundColor: "rgba(0,0,0,0.35)",
                     border: "1px solid rgba(255,255,255,0.08)" }}>
            <div className="flex items-center gap-1.5">
              <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">
                LTV / CAC
              </label>
              <span title="LTV/CAC = a payback multiple. 1× means each customer just repays their acquisition cost; funders typically want ≥ 3× for a healthy consumer/SMB business, and ≥ 5× for enterprise. Below 1× signals you're burning cash on every customer."
                className="inline-flex items-center justify-center rounded-full"
                style={{ width: 14, height: 14, backgroundColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.6)" }}>
                <Info size={9} weight="bold" />
              </span>
            </div>
            <div className="mt-2">
              <div
                data-testid="unit-econ-ltv-cac"
                className="font-extrabold font-mono-num"
                style={{
                  color: ltvCacRatio == null
                    ? "rgba(255,255,255,0.35)"
                    : ltvCacRatio >= 3 ? "#10B981"
                      : ltvCacRatio >= 1 ? "#F59E0B"
                        : "#EF4444",
                  fontSize: "28px",
                  letterSpacing: "-0.02em",
                  lineHeight: 1,
                }}
              >
                {ltvCacRatio == null ? "—" : `${ltvCacRatio.toFixed(1)}x`}
              </div>
              <div className="mt-1 text-[10.5px] text-white/45">
                Auto-calculated: LTV ÷ CAC
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save projections — moved BELOW the Unit Economics block (iter72)
          so a single Save commits both the year rows and the CAC/LTV
          values in one action, and the button sits at the natural end
          of the form. */}
      <div className="pt-2 flex justify-end">
        <button onClick={save} disabled={saving || rows.length === 0} data-testid="proj-save"
          className="px-4 py-2 rounded-md text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60"
          style={{ backgroundColor: C.teal, color: "#fff" }}>
          <FloppyDisk size={14} weight="fill" /> {saving ? "Saving…" : "Save projections"}
        </button>
      </div>
    </div>
  );
}

// Small unit-economics input tile used by the Financial Projections
// block (iter70). Renders a currency-prefixed number input + a
// Current/Forecast dropdown + a hover-info tooltip. Kept tiny and
// stateless so the parent owns { value, kind }.
function UnitEconInput({ testidPrefix, label, title, info, currency, value, kind, onValue, onKind }) {
  return (
    <div className="rounded-lg p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.08)" }}
      data-testid={`${testidPrefix}-tile`}>
      <div className="flex items-center gap-1.5 mb-2">
        <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">
          {label}
        </label>
        <span title={info}
          className="inline-flex items-center justify-center rounded-full"
          style={{ width: 14, height: 14, backgroundColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.6)" }}
          data-testid={`${testidPrefix}-info`}>
          <Info size={9} weight="bold" />
        </span>
        <span className="text-[10px] text-white/40 italic">{title}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-white/45 font-mono-num shrink-0">{currency}</span>
        {/* Iter100: reuse the Projections MoneyInput so CAC/LTV shows
            thousand-separator formatting on blur, raw digits on focus.
            Bridges the string → number contract of UnitEconInput. */}
        <MoneyInput
          value={value || 0}
          onChange={(raw) => {
            const v = parseFloat(raw || "0");
            onValue(Number.isFinite(v) ? v : 0);
          }}
          testid={`${testidPrefix}-value`}
        />
      </div>
      <div className="mt-2">
        <select
          value={kind || "current"}
          onChange={(e) => onKind(e.target.value)}
          data-testid={`${testidPrefix}-kind`}
          className="w-full px-2 py-1.5 rounded text-[11.5px] text-white/85 focus:outline-none"
          style={{ backgroundColor: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.1)" }}
        >
          <option value="current">Current</option>
          <option value="forecast">Forecast</option>
        </select>
      </div>
    </div>
  );
}



/* Iter102: rotating draft-phase status strings. Kept as a module-level
   constant so the reusable <PhaseIndicator> can consume them wherever the
   founder triggers a Claude draft. */
const AI_DRAFT_PHASES = [
  "Reading your business context…",
  "Weighing methodology signals…",
  "Sketching the funder framing…",
  "Cross-checking industry benchmarks…",
  "Drafting punchy bullets…",
  "Polishing the tone…",
];

/* Iter103: status strings shown during the ~30-60s AI mapping step after
   an existing pitch deck is uploaded. */
const IMPORT_PROCESSING_PHASES = [
  "Extracting slides from your file…",
  "Reading each slide's content…",
  "Matching against the 11-slide methodology…",
  "Migrating overflow content to sub-slides…",
  "Preserving your numbers, sources & names…",
  "Finalising the mapped deck…",
];

/* Iter103: status strings shown during the ~8-20s team photo upload +
   processing pipeline (image resize, storage, thumbnail generation). */
const TEAM_PHOTO_UPLOAD_PHASES = [
  "Uploading photo…",
  "Optimising image…",
  "Saving to secure storage…",
  "Almost there…",
];

/* Iter103: status strings shown during the ~10-15s Nano Banana photo
   generation for a slide or sub-slide. */
const AI_PHOTO_GEN_PHASES = [
  "Composing the scene…",
  "Generating with Nano Banana…",
  "Refining the details…",
  "Finalising the image…",
];

/* Iter99: focus-aware money input.
   • Blurred → renders the value with thousand separators (120,000,000)
     for scan-friendly reading in the projections table.
   • Focused → renders the raw integer so editing / arrow-key stepping
     works naturally without commas getting in the way.
   Delegates to the parent via `onChange(rawString)` on every keystroke —
   parent stores the cleaned digit-only value. */
function MoneyInput({ value, onChange, testid }) {
  const [focused, setFocused] = React.useState(false);
  const fmt = (v) => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    if (!isFinite(n)) return "";
    return n.toLocaleString("en-US");
  };
  const display = focused ? String(value ?? 0) : fmt(value);
  return (
    <input
      // While focused we accept raw digits + arrow keys, so we keep
      // `type="number"`. On blur we switch to "text" so
      // toLocaleString's commas render (number inputs strip commas).
      type={focused ? "number" : "text"}
      value={display}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        // Strip anything that isn't a digit / minus / dot so paste of
        // "1,234.56" or "1 234" from a spreadsheet still lands cleanly.
        const cleaned = e.target.value.replace(/[^0-9.\-]/g, "");
        onChange(cleaned);
      }}
      data-testid={testid}
      className="flex-1 min-w-0 px-2 py-1.5 text-right text-[13px] text-white font-mono-num focus:outline-none bg-transparent"
      style={{ border: "none" }}
    />
  );
}

function ProjRow({ label, rows, field, onChange, editable, derived, isPct, bold, nullable, currency, testid }) {
  return (
    <tr className="border-t border-white/5">
      <td className="px-3 py-2.5 text-[12px] font-semibold"
        style={{ color: derived ? "#F59E0B" : "rgba(255,255,255,0.85)" }}>
        {label}{derived && <span className="text-[9px] uppercase tracking-wider text-white/35 ml-1.5">auto</span>}
      </td>
      {rows.map((r, i) => {
        const raw = r[field];
        if (editable) {
          return (
            <td key={i} className="px-2 py-1.5 text-center">
              {/* Iter98: inline currency badge on money-typed inputs so
                  founders never confuse the value's unit. Mirrors the
                  iter97 Capital Ask 2-field control at a smaller scale. */}
              <div className="flex items-stretch rounded overflow-hidden focus-within:ring-1"
                style={{ border: "1px solid rgba(255,255,255,0.1)", backgroundColor: "rgba(0,0,0,0.4)" }}>
                {currency && (
                  <span className="flex items-center justify-center px-1.5 text-[10px] font-mono-num font-bold tracking-wider select-none shrink-0"
                    style={{ backgroundColor: "rgba(10,139,133,0.28)", color: "#7DD3D0", minWidth: 34 }}
                    title={`Amount in ${currency}`}>
                    {currency}
                  </span>
                )}
                <MoneyInput
                  value={raw ?? 0}
                  onChange={(v) => onChange(i, field, v)}
                  testid={`${testid}-${i}`}
                />
              </div>
            </td>
          );
        }
        let display;
        if (raw === null || raw === undefined) {
          display = "—";
        } else if (isPct) {
          const sign = (field === "growth" && raw >= 0) ? "+" : "";
          display = `${sign}${Number(raw).toFixed(1)}%`;
        } else {
          display = Math.round(Number(raw)).toLocaleString();
        }
        return (
          <td key={i} className="px-3 py-2.5 text-center font-mono-num text-[13px]"
            data-testid={`${testid}-display-${i}`}
            style={{ color: bold ? "#F59E0B" : "rgba(255,255,255,0.85)",
                     fontWeight: bold ? 700 : 500 }}>
            {display}
          </td>
        );
      })}
    </tr>
  );
}



function FailureCheckView({ check, onBack, onExport, onFix }) {
  if (!check) return null;
  const total = check.total;
  const passed = check.passed_count;
  const ready = check.funder_ready;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="failure-check-view">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white mb-6">
        <ArrowLeft size={14} /> Back to slides
      </button>
      <div className="text-[11px] uppercase tracking-[0.25em] font-semibold mb-3" style={{ color: C.orange }}>The 10 Failure Registry · Pre-submission gate</div>
      <h1 className="font-extrabold text-4xl tracking-tight" style={{ letterSpacing: "-0.03em" }}>
        {ready ? <>You are <span style={{ color: C.teal }}>funder-ready</span>.</>
               : <>{total - passed} block{total - passed !== 1 ? "s" : ""} between you and <span style={{ color: C.orange }}>funder-ready</span>.</>}
      </h1>
      <p className="text-white/60 mt-3 max-w-2xl">
        The 10 checks below mirror what funders actually look for. Every one must pass before you submit.
      </p>

      <div className="mt-6 mb-8 p-5 rounded-xl border"
        style={{ backgroundColor: ready ? "rgba(44, 197, 168, 0.08)" : "rgba(245, 158, 11, 0.08)",
                 borderColor: ready ? C.teal : C.orange }}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-3xl font-extrabold">{passed} / {total}</div>
            <div className="text-sm text-white/70 mt-1">{ready ? "All checks passed" : "Checks remaining"}</div>
          </div>
          {ready && (
            <button onClick={onExport} data-testid="check-export-btn"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md font-semibold text-sm transition-colors"
              style={{ backgroundColor: C.teal, color: "#fff" }}>
              <DownloadSimple size={14} /> Export .pptx
            </button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {check.checks.map((c) => {
          const clickable = !c.passed && c.slide_num && onFix;
          return (
          <div key={c.code} data-testid={`check-${c.code}`}
            onClick={clickable ? () => onFix(c) : undefined}
            role={clickable ? "button" : undefined}
            className={`p-4 rounded-lg flex items-start gap-3 transition-colors ${clickable ? "cursor-pointer hover:bg-white/[0.04]" : ""}`}
            style={{ backgroundColor: c.passed ? "rgba(44, 197, 168, 0.05)" : "rgba(245, 158, 11, 0.05)",
                     border: `1px solid ${c.passed ? "rgba(44, 197, 168, 0.3)" : "rgba(245, 158, 11, 0.3)"}` }}>
            {c.passed ? <CheckCircle size={20} weight="fill" style={{ color: C.teal, marginTop: 2, flexShrink: 0 }} />
                      : <XCircle size={20} weight="fill" style={{ color: C.orange, marginTop: 2, flexShrink: 0 }} />}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase text-white/40">{c.code}</span>
                <span className={`text-[10px] font-bold uppercase tracking-widest`}
                  style={{ color: c.passed ? C.teal : C.orange }}>{c.passed ? "PASS" : "FAIL"}</span>
              </div>
              <div className="text-sm text-white/85 mt-1">{c.reason}</div>
            </div>
            {clickable && (
              <span data-testid={`check-fix-btn-${c.code}`}
                className="self-center flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded-md whitespace-nowrap"
                style={{ backgroundColor: "rgba(245,158,11,0.14)", color: C.orange, border: `1px solid rgba(245,158,11,0.4)` }}>
                Fix it <CaretRight size={11} weight="bold" />
              </span>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}



/* Final wizard step — big Generate button + share link + progress recap. */
function WizardFinalStep({ deck, onBack, onGenerate, onShare, creating, onJumpToSlide }) {
  const filledSlides = (deck.slides || []).filter(
    (s) => s.num !== 7
      && ((s.content || "").trim().length > 20
        || (s.num === 9 && (deck.team || []).length > 0)
        || (s.num === 10 && ((deck.projections || {}).years || []).length > 0)
        || (s.num === 12 && ((deck.closing || {}).email || (deck.closing || {}).website)))
  ).length;
  const isReady = filledSlides >= 10;
  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-8" data-testid="wizard-final-step">
      <div className="text-[11px] uppercase tracking-[0.25em] font-bold mb-3" style={{ color: C.tealSoft }}>
        Step {TOTAL_STEPS} of {TOTAL_STEPS} · Generate & download
      </div>
      <h1 className="font-extrabold text-4xl sm:text-5xl tracking-tight" style={{ letterSpacing: "-0.03em" }}>
        You made it. <span style={{ color: C.teal }}>Time to deliver.</span>
      </h1>
      <p className="text-white/60 mt-3 max-w-2xl">
        Your deck runs through the Clinq Design v2 templates on export — infographic layouts, branded charts,
        word-count guards, and a footer on every slide. One click and you have the .pptx.
      </p>

      <div className="mt-8">
        <ReviewGateCard deck={deck} onJump={onJumpToSlide} />
      </div>

      <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatBox label="Core slides filled" value={`${filledSlides} / ${CORE_SLIDES_COUNT}`} accent={isReady ? "teal" : "orange"} />
        <StatBox label="Sub-slides added" value={String((deck.slides || []).reduce((a, s) => a + ((s.extras || []).length), 0))} />
        <StatBox label="Team members" value={String((deck.team || []).length)} />
      </div>

      {!isReady && (
        <div className="mt-6 rounded-lg p-4 flex items-start gap-2.5"
          style={{ backgroundColor: "rgba(245, 158, 11, 0.08)", border: "1px solid rgba(245, 158, 11, 0.28)" }}
          data-testid="wizard-readiness-warning">
          <Sparkle size={14} weight="fill" style={{ color: C.orange, marginTop: 2 }} />
          <div className="text-[12.5px] text-white/75 leading-snug">
            <span className="font-semibold text-white">Only {filledSlides}/{CORE_SLIDES_COUNT} core slides are filled.</span> You can still generate now, but funders will spot the gaps. Consider going back and completing them for a stronger deck.
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <button onClick={onGenerate} disabled={creating} data-testid="wizard-generate-btn"
          className="text-base font-bold px-6 py-3 rounded-md inline-flex items-center gap-2 transition-colors disabled:opacity-60"
          style={{ backgroundColor: C.teal, color: "#fff" }}>
          <DownloadSimple size={18} weight="bold" />
          {creating ? "Generating…" : "Generate & download deck · 25 cr"}
        </button>
        <button onClick={onShare} data-testid="wizard-share-btn"
          className="text-sm font-semibold px-4 py-3 rounded-md inline-flex items-center gap-2 hover:bg-white/5 transition-colors"
          style={{ color: C.tealSoft, border: "1px solid rgba(123, 211, 206, 0.35)" }}>
          <Share size={14} weight="bold" /> Share read-only link
        </button>
      </div>

      <WizardFooterNav onBack={onBack} onNext={onGenerate} nextLabel={creating ? "Generating…" : "Download deck"} />
    </div>
  );
}

function StatBox({ label, value, accent }) {
  const color = accent === "teal" ? C.teal : accent === "orange" ? C.orange : "#fff";
  return (
    <div className="rounded-lg p-4 border"
      style={{ backgroundColor: "rgba(10, 40, 38, 0.5)", borderColor: "rgba(123, 211, 206, 0.12)" }}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/45">{label}</div>
      <div className="mt-1 font-extrabold text-3xl font-mono-num tracking-tight" style={{ color }}>{value}</div>
    </div>
  );
}
